import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import OverallCohortChart from './OverallCohortChart';
import HospitalDistributionChart from './HospitalDistributionChart';
// import LinesOfFailure from './LinesOfFailure';
import Global2 from './Global2';
import SOCBenchmark from './SOCBenchmark';
import PersonaDeepDiveModal, { InlinePatientTable } from './PersonaDeepDiveModal';
import GlossaryLink from './common/GlossaryLink';
import { formatHospitalDisplay } from '../utils/formatHospitalName';
// import APIStatus from './common/APIStatus';
import { useSurvivalAnalysis } from '../hooks/useSurvivalAnalysis';
import { useFiltersData } from '../hooks/useFiltersData';
import { useSafetyProfile } from '../hooks/useSafetyProfile';
import { useRbacFilters } from '../hooks/useRbacFilters';
import { getStoredUser } from '../utils/auth';
import { post } from '../utils/api';
import { API_ENDPOINTS } from '../config/api';
// import { fetchSurvivalAnalysis } from '../services/survivalAnalysisService';
import { fetchORRData } from '../services/survivalAnalysisService';
// Import test utilities for debugging
import { testAPIConnection, testSurvivalAnalysis } from '../utils/apiTest';
import { testOverallCohortAPI } from '../utils/testOverallCohort';
import MonthYearPicker from './common/MonthYearPicker';
import { useFilterContext } from './layout/FilterContext';
import {
  Users, TrendingUp, TrendingDown,
  Filter, ChevronLeft, Calendar, CircleDot, Circle, Scale,
  ChevronDown, Search, X, ChevronUp, Info
} from 'lucide-react';

/**
 * NexCAR19 Unified Dashboard
 * Modules:
 * 1. Patient Personas: Detailed drill-down with granular filters.
 * 2. Benchmarking: Comparative analysis against SOC and Global CAR-T.
 */

// --- Constants & Mock Data ---
// Fallback data - will be replaced by real data from API
const FALLBACK_FILTERS_DATA = {
  institutes: ["Tata Memorial Center", "Apollo Mumbai", "AIIMS Delhi", "Max Healthcare", "Fortis Memorial", "CMC Vellore"],
  regions: ["North India", "West Zone", "South Zone", "East Zone", "Central India"],
  physicians: ["Dr. Anjali Gupta", "Dr. Rajesh Patil", "Dr. Vikram Singh", "Dr. Meera Reddy", "Dr. S. K. Sharma", "Dr. Kabir Khan"]
};

// SOC Regimens List
const BENCHMARK_REGIMENS = {
  NHL: ["Salvage + BMT", "R-ESHAP", "Tafasitamab + Lenalidomide", "BMT (Standard)", "POLA + BR"],
  "B-ALL": ["Inotuzumab ozogamicin", "Imatinib mesylate", "Ph+ FLAG (Flud/Cyt/G-CSF)", "Ponatinib mesylate", "PH- Inotuzumab + Mini-hyper-CVD + Blina"]
};

// Continuous Gradient Configuration - True gradient with smooth transitions
const GRADIENT_CONFIG = {
  // Define gradient stops for smooth color transitions
  clinical: {
    // Simple 2-color gradient: amber/yellow (0-25%) to green (26-100%)
    stops: [
      { percent: 0, color: [255, 140, 0] },    // Dark orange/amber #FF8C00
      { percent: 25, color: [255, 200, 0] },   // Yellow-orange #FFC800 (transition point)
      { percent: 100, color: [34, 139, 34] }   // Forest green #228B22
    ]
  },
  viridis: {
    // Viridis color scheme - popular in scientific visualization
    stops: [
      { percent: 0, color: [68, 1, 84] },      // Dark purple
      { percent: 25, color: [59, 82, 139] },   // Blue-purple
      { percent: 50, color: [33, 145, 140] },  // Teal
      { percent: 75, color: [94, 201, 98] },   // Green
      { percent: 100, color: [253, 231, 37] }  // Yellow
    ]
  },
  plasma: {
    // Plasma color scheme - another scientific favorite
    stops: [
      { percent: 0, color: [13, 8, 135] },     // Dark blue
      { percent: 25, color: [126, 3, 168] },   // Purple
      { percent: 50, color: [203, 70, 121] },  // Pink
      { percent: 75, color: [248, 149, 64] },  // Orange
      { percent: 100, color: [240, 249, 33] }  // Yellow
    ]
  }
};

// Linear interpolation between two colors

// ─── Mouse-following tooltip for hourglass icon ───────────────────────────────
const HourglassTooltip = () => {
  const [pos, setPos] = React.useState(null);
  return (
    <span
      className="inline-flex items-center justify-center cursor-default"
      onMouseEnter={e => setPos({ x: e.clientX, y: e.clientY })}
      onMouseMove={e => setPos({ x: e.clientX, y: e.clientY })}
      onMouseLeave={() => setPos(null)}
    >
      <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-slate-400 shrink-0">
        <path d="M5 22h14"/><path d="M5 2h14"/>
        <path d="M17 22v-4.172a2 2 0 0 0-.586-1.414L12 12l-4.414 4.414A2 2 0 0 0 7 17.828V22"/>
        <path d="M7 2v4.172a2 2 0 0 0 .586 1.414L12 12l4.414-4.414A2 2 0 0 0 17 6.172V2"/>
      </svg>
      {pos && typeof document !== 'undefined' && createPortal(
        <div
          className="fixed z-[99999] w-52 px-2.5 py-1.5 rounded-lg bg-slate-800 text-white text-[10px] leading-snug font-normal shadow-lg whitespace-normal text-center pointer-events-none"
          style={{ top: pos.y - 60, left: pos.x - 100 }}
        >
          Patients exist but have not yet reached the selected evaluation timepoint
        </div>,
        document.body
      )}
    </span>
  );
};
const interpolateColor = (color1, color2, factor) => {
  const result = color1.slice();
  for (let i = 0; i < 3; i++) {
    result[i] = Math.round(result[i] + factor * (color2[i] - result[i]));
  }
  return result;
};

// Get exact color for any percentage (0-100) with smooth gradient
const getContinuousGradientColor = (percentage, scheme = 'clinical') => {
  // Clamp percentage between 0 and 100
  const clampedPercent = Math.max(0, Math.min(100, percentage));

  const stops = GRADIENT_CONFIG[scheme].stops;

  // Find the two stops that bracket our percentage
  let lowerStop = stops[0];
  let upperStop = stops[stops.length - 1];

  for (let i = 0; i < stops.length - 1; i++) {
    if (clampedPercent >= stops[i].percent && clampedPercent <= stops[i + 1].percent) {
      lowerStop = stops[i];
      upperStop = stops[i + 1];
      break;
    }
  }

  // Calculate interpolation factor
  const range = upperStop.percent - lowerStop.percent;
  const factor = range === 0 ? 0 : (clampedPercent - lowerStop.percent) / range;

  // Interpolate between the two colors
  const interpolatedColor = interpolateColor(lowerStop.color, upperStop.color, factor);

  // Return as hex color
  return `rgb(${interpolatedColor[0]}, ${interpolatedColor[1]}, ${interpolatedColor[2]})`;
};

// Helper function to determine text color for contrast
const getContrastTextColor = (percentage) => {
  // For lower percentages (darker colors), use white text
  // For higher percentages (brighter colors), use dark text
  return percentage < 50 ? 'text-slate-900' : 'text-white';
};

// --- Overall Cohort Calculation Functions ---
// Calculate weighted average for overall cohort (13th curve for NHL, 5th curve for B-ALL)
// const calculateOverallCohort = (dataset, analysisType) => {
//   let totalPatients = 0;
//   let weightedPfsSum = 0;
//   let weightedOsSum = 0;

//   dataset.rows.forEach(row => {
//     dataset.columns.forEach(col => {
//       const cellData = row.values[col.id];
//       const patientCount = cellData.n;

//       weightedPfsSum += (cellData.pfs * patientCount);
//       weightedOsSum += (cellData.os * patientCount);
//       totalPatients += patientCount;
//     });
//   });

//   return {
//     pfs: Math.round(weightedPfsSum / totalPatients),
//     os: Math.round(weightedOsSum / totalPatients),
//     totalPatients: totalPatients,
//     // Create mock data for Kaplan-Meier modal
//     n: totalPatients,
//     title: 'Overall Cohort',
//     description: `Combined analysis across all ${dataset.rows.length * dataset.columns.length} patient subgroups`
//   };
// };

// --- DATA GENERATORS ---
// Helper to generate mock data based on a base NexCAR dataset but skewed for SOC/Global
const generateBenchmarkData = (baseData, modifier = 0.8) => {
  const newData = JSON.parse(JSON.stringify(baseData)); // Deep copy
  if (newData.rows) {
    newData.rows.forEach(row => {
      Object.keys(row.values).forEach(key => {
        // Mock logic: SOC is usually lower efficacy, Global CAR-T is comparable/higher
        // Randomize slightly to look real
        const variance = (Math.random() * 0.2) - 0.1; // +/- 10%
        const factor = modifier + variance;
        row.values[key].pfs = Math.min(100, Math.max(0, Math.round(row.values[key].pfs * factor)));
        if (row.values[key].os) {
          row.values[key].os = Math.min(100, Math.max(0, Math.round(row.values[key].os * factor)));
        }
      });
    });
  }
  return newData;
};

// Base NexCAR Data - matching your exact image values with OS data added
const DATA_NHL_NEXCAR = {
  totalPatients: 122,
  columns: [
    { id: 'nb_low', label: 'Low Risk', sub: 'Non-Bulky', type: 'non-bulky', range: '0-2' },
    { id: 'nb_high', label: 'High Risk', sub: 'Non-Bulky', type: 'non-bulky', range: '3-5' },
    { id: 'b_low', label: 'Low Risk', sub: 'Bulky', type: 'bulky', range: '0-2' },
    { id: 'b_high', label: 'High Risk', sub: 'Bulky', type: 'bulky', range: '3-5' },
  ],
  rows: [
    {
      id: "refractory",
      title: "Primary Refractory",
      totalN: 47,
      desc: "No Response",
      values: {
        nb_low: { pfs: 48, os: 52, n: 30 },
        nb_high: { pfs: 17, os: 22, n: 5 },
        b_low: { pfs: 60, os: 65, n: 18 },
        b_high: { pfs: 25, os: 30, n: 5 }
      }
    },
    {
      id: "early_relapse",
      title: "Early Relapsed",
      totalN: 54,
      desc: "< 12 Months",
      values: {
        nb_low: { pfs: 57, os: 61, n: 36 },
        nb_high: { pfs: 25, os: 30, n: 6 },
        b_low: { pfs: 33, os: 38, n: 6 },
        b_high: { pfs: 40, os: 45, n: 5 }
      }
    },
    {
      id: "late_relapse",
      title: "Late Relapsed",
      totalN: 21,
      desc: ">= 12 Months",
      values: {
        nb_low: { pfs: 86, os: 88, n: 28 },
        nb_high: { pfs: 100, os: 100, n: 5 },
        b_low: { pfs: 60, os: 63, n: 5 },
        b_high: { pfs: 33, os: 40, n: 5 }
      }
    }
  ]
};

const DATA_BALL_NEXCAR = {
  totalPatients: 88,
  columns: [
    { id: 'mrd_neg', label: 'MRD -ve', sub: 'MRD -ve', type: 'mrd_neg', range: null },
    { id: 'mrd_pos', label: 'MRD +ve', sub: 'MRD +ve', type: 'mrd_pos', range: null },
    { id: 'blast', label: 'Blast ≥5%', sub: 'Blast >=5%', type: 'blast', range: null }
  ],
  rows: [
    {
      id: "refractory_early",
      title: "Refractory",
      linesLabel: "Early",
      desc: "1-2 prior lines",
      totalN: 25,
      values: {
        mrd_neg: { pfs: 70, os: 75, n: 10 },
        mrd_pos: { pfs: 55, os: 60, n: 15 },
        blast: { pfs: 40, os: 45, n: 7 }
      }
    },
    {
      id: "refractory_late",
      title: "Refractory",
      linesLabel: "Late",
      desc: "3+ prior lines",
      totalN: 15,
      values: {
        mrd_neg: { pfs: 60, os: 65, n: 6 },
        mrd_pos: { pfs: 45, os: 50, n: 9 },
        blast: { pfs: 30, os: 35, n: 4 }
      }
    },
    {
      id: "relapsed_early",
      title: "Relapse",
      linesLabel: "Early",
      desc: "1-2 prior lines",
      totalN: 30,
      values: {
        mrd_neg: { pfs: 65, os: 70, n: 12 },
        mrd_pos: { pfs: 50, os: 55, n: 18 },
        blast: { pfs: 35, os: 40, n: 8 }
      }
    },
    {
      id: "relapsed_late",
      title: "Relapse",
      linesLabel: "Late",
      desc: "3+ prior lines",
      totalN: 18,
      values: {
        mrd_neg: { pfs: 55, os: 60, n: 7 },
        mrd_pos: { pfs: 40, os: 45, n: 11 },
        blast: { pfs: 25, os: 30, n: 5 }
      }
    }
  ]
};

// --- Shared Components ---
// const Badge = ({ children, color = "slate" }) => {
//   const colors = {
//     slate: "bg-slate-100 text-slate-700",
//     teal: "bg-teal-50 text-teal-700",
//     blue: "bg-blue-50 text-blue-700",
//     rose: "bg-rose-50 text-rose-700",
//     amber: "bg-amber-50 text-amber-700",
//     indigo: "bg-indigo-50 text-indigo-700"
//   };
//   return (
//     <span className={`px-2.5 py-1 rounded-full text-sm mt-2 font-semibold ${colors[color] || colors.slate}`}>
//       {children}
//     </span>
//   );
// };

// --- Component: Searchable Institute Dropdown ---
const SearchableInstituteDropdown = ({
  institutes,
  selectedInstitute,
  onSelect,
  placeholder = "Search institutes..."
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);

  // Filter institutes based on search term
  const filteredInstitutes = useMemo(() => {
    if (!searchTerm) return institutes;
    return institutes.filter(institute =>
      institute.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [institutes, searchTerm]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (institute) => {
    onSelect(institute);
    setIsOpen(false);
    setSearchTerm('');
  };

  const clearSelection = () => {
    onSelect('');
    setSearchTerm('');
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Selected Institute Display / Search Input */}
      <div
        className="w-full bg-slate-50 border border-slate-200 text-slate-700 text-xs rounded-lg focus-within:ring-teal-500 focus-within:border-teal-500 p-2 cursor-pointer"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex items-center justify-between">
          {selectedInstitute ? (
            <div className="flex items-center gap-2 flex-1">
              <span className="text-ellipsis">{formatHospitalDisplay(selectedInstitute)}</span>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  clearSelection();
                }}
                className="text-slate-400 hover:text-slate-600 flex-shrink-0"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <span className="text-slate-400">{placeholder}</span>
          )}
          <div className="text-slate-500 flex-shrink-0">
            {isOpen ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
          </div>
        </div>
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 overflow-hidden">
          {/* Search Input */}
          <div className="p-2 border-b border-slate-100">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-3 h-3 text-slate-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search institutes..."
                className="w-full pl-7 pr-3 py-1.5 text-xs border border-slate-200 rounded focus:ring-teal-500 focus:border-teal-500"
                autoFocus
              />
            </div>
          </div>

          {/* Institute List */}
          <div className="max-h-40 overflow-y-auto">
            {filteredInstitutes.length > 0 ? (
              filteredInstitutes.map((institute) => (
                <div
                  key={institute}
                  onClick={() => handleSelect(institute)}
                  className={`px-3 py-2 text-xs cursor-pointer hover:bg-slate-50 transition-colors ${selectedInstitute === institute ? 'bg-teal-50 text-teal-700 font-medium' : 'text-slate-700'
                    }`}
                >
                  {formatHospitalDisplay(institute)}
                </div>
              ))
            ) : (
              <div className="px-3 py-2 text-xs text-slate-400 italic">
                No institutes found
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// --- Component: Searchable Single-Select Physicians ---
const SearchablePhysicianDropdown = ({
  physicians,
  selectedPhysician,
  onSelect,
  placeholder = "Search physicians..."
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);

  // Filter physicians based on search term
  const filteredPhysicians = useMemo(() => {
    if (!searchTerm) return physicians;
    return physicians.filter(physician =>
      physician.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [physicians, searchTerm]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (physician) => {
    onSelect(physician);
    setIsOpen(false);
    setSearchTerm('');
  };

  const clearSelection = () => {
    onSelect('');
    setSearchTerm('');
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Selected Physician Display / Search Input */}
      <div
        className="w-full bg-slate-50 border border-slate-200 text-slate-700 text-xs rounded-lg focus-within:ring-teal-500 focus-within:border-teal-500 p-2 cursor-pointer"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex items-center justify-between">
          {selectedPhysician ? (
            <div className="flex items-center gap-2 flex-1">
              <span className="truncate">{selectedPhysician}</span>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  clearSelection();
                }}
                className="text-slate-400 hover:text-slate-600 flex-shrink-0"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ) : (
            <span className="text-slate-400">{placeholder}</span>
          )}
          <div className="text-slate-500 flex-shrink-0">
            {isOpen ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
          </div>
        </div>
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 overflow-hidden">
          {/* Search Input */}
          <div className="p-2 border-b border-slate-100">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-3 h-3 text-slate-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search physicians..."
                className="w-full pl-7 pr-3 py-1.5 text-xs border border-slate-200 rounded focus:ring-teal-500 focus:border-teal-500"
                autoFocus
              />
            </div>
          </div>

          {/* Physician List */}
          <div className="max-h-40 overflow-y-auto">
            {filteredPhysicians.length > 0 ? (
              filteredPhysicians.map((physician) => (
                <div
                  key={physician}
                  onClick={() => handleSelect(physician)}
                  className={`px-3 py-2 text-xs cursor-pointer hover:bg-slate-50 transition-colors ${selectedPhysician === physician ? 'bg-teal-50 text-teal-700 font-medium' : 'text-slate-700'
                    }`}
                >
                  {physician}
                </div>
              ))
            ) : (
              <div className="px-3 py-2 text-xs text-slate-400 italic">
                No physicians found
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// --- Component: Overall Cohort Card ---
// const OverallCohortCard = ({ dataset, analysisType, gradientScheme, onCardClick, indication }) => {
//   // Use real backend data if available, otherwise fallback to frontend calculation
//   const overallCohort = dataset.overallCohort || calculateOverallCohort(dataset, analysisType);
//   const currentValue = analysisType.toLowerCase() === 'pfs' ? overallCohort.pfs : overallCohort.os;
//   const textColor = getContrastTextColor(currentValue);

//   // Determine data source for display
//   const isRealData = !!dataset.overallCohort;

//   return (
//     <div className="bg-white rounded-xl shadow-lg border-2 border-amber-200 p-6 mb-6 relative overflow-hidden">

//       <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-amber-400 via-yellow-500 to-amber-600"></div>

//       <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">

//         <div className="flex-1">
//           <div className="flex items-center gap-3 mb-2">
//             <div className="bg-amber-100 rounded-full p-2">
//               <Users className="w-6 h-6 text-amber-700" />
//             </div>
//             <div>
//               <h2 className="text-2xl font-bold text-slate-900">
//                 🎯 Overall Cohort - {indication}
//               </h2>
//               <p className="text-sm text-slate-600">
//                 {overallCohort.description || `Combined analysis across all patient subgroups`}
//               </p>
//             </div>
//           </div>
//           <div className="text-xs text-slate-500 bg-slate-50 rounded-lg p-3 mt-3">
//             <div className="flex items-center gap-2 mb-2">
//               <strong>Data Source:</strong> 
//               <span className={`px-2 py-1 rounded text-xs font-medium ${isRealData ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
//                 {isRealData ? '🔬 Backend Kaplan-Meier Analysis' : '📊 Frontend Weighted Average'}
//               </span>
//             </div>
//             <strong>Clinical Significance:</strong> {isRealData ? 'Real survival analysis using lifelines KaplanMeierFitter' : 'Weighted average based on patient distribution across all risk categories and disease characteristics'}. 
//             This represents the overall treatment efficacy for the entire patient population.
//           </div>
//         </div>

//         {/* Right side - Metrics */}
//         <div className="flex flex-col sm:flex-row gap-4 lg:gap-6">
//           {/* PFS Card */}
//           <div
//             className={`relative cursor-pointer transition-all hover:scale-105 hover:shadow-xl rounded-xl p-4 min-w-32 ${textColor === 'white' ? 'text-white' : 'text-slate-900'}`}
//             style={{ backgroundColor: getContinuousGradientColor(overallCohort.pfs, gradientScheme) }}
//             onClick={() => onCardClick && onCardClick(
//               { ...overallCohort, pfs: overallCohort.pfs, os: overallCohort.os },
//               { title: 'Overall Cohort', desc: overallCohort.description },
//               { label: 'All Patients', sub: 'Combined' }
//             )}
//           >
//             <div className="text-center">
//               <div className="text-xs font-bold opacity-80 mb-1">PFS</div>
//               <div className="text-3xl font-bold tracking-tight leading-none drop-shadow-sm">
//                 {overallCohort.pfs}%
//               </div>
//               <div className="text-xs opacity-75 mt-1">n = {overallCohort.totalPatients}</div>
//             </div>
//             {/* Subtle gradient overlay */}
//             <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-black/5 rounded-xl pointer-events-none" />
//           </div>

//           {/* OS Card */}
//           <div
//             className={`relative cursor-pointer transition-all hover:scale-105 hover:shadow-xl rounded-xl p-4 min-w-32 ${getContrastTextColor(overallCohort.os) === 'white' ? 'text-white' : 'text-slate-900'}`}
//             style={{ backgroundColor: getContinuousGradientColor(overallCohort.os, gradientScheme) }}
//             onClick={() => onCardClick && onCardClick(
//               { ...overallCohort, pfs: overallCohort.pfs, os: overallCohort.os },
//               { title: 'Overall Cohort', desc: overallCohort.description },
//               { label: 'All Patients', sub: 'Combined' }
//             )}
//           >
//             <div className="text-center">
//               <div className="text-xs font-bold opacity-80 mb-1">OS</div>
//               <div className="text-3xl font-bold tracking-tight leading-none drop-shadow-sm">
//                 {overallCohort.os}%
//               </div>
//               <div className="text-xs opacity-75 mt-1">n = {overallCohort.totalPatients}</div>
//             </div>
//             {/* Subtle gradient overlay */}
//             <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-black/5 rounded-xl pointer-events-none" />
//           </div>
//         </div>
//       </div>

//       {/* Bottom info bar */}
//       <div className="mt-4 pt-4 border-t border-amber-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-xs text-slate-600">
//         <div className="flex items-center gap-4">
//           {isRealData ? (
//             <>
//               <span>🔬 Kaplan-Meier analysis</span>
//               <span>📊 Lifelines library</span>
//               <span>📈 Real survival curves</span>
//             </>
//           ) : (
//             <>
//               <span>📊 Weighted by patient count</span>
//               <span>🔬 Clinically validated</span>
//               <span>📈 Publication ready</span>
//             </>
//           )}
//         </div>
//         <div className="text-amber-700 font-medium">
//           Click cards to view {isRealData ? 'real' : 'combined'} survival curves
//         </div>
//       </div>
//     </div>
//   );
// };

// --- Component: Heat Map Legend ---
// const HeatMapLegend = ({ scheme = 'clinical' }) => {
//   const generateGradientCSS = (scheme) => {
//     const stops = GRADIENT_CONFIG[scheme].stops;
//     const gradientStops = stops.map(stop =>
//       `rgb(${stop.color[0]}, ${stop.color[1]}, ${stop.color[2]}) ${stop.percent}%`
//     ).join(', ');
//     return `linear-gradient(to right, ${gradientStops})`;
//   };

//   return (
//     <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-4 mb-4">
//       <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">
//         Continuous Color Gradient - Card View
//       </div>
//       <div className="flex items-center gap-4">
//         {/* Continuous Gradient Bar */}
//         <div className="flex-1">
//           <div
//             className="h-8 rounded-lg border border-slate-200"
//             style={{ background: generateGradientCSS(scheme) }}
//           />
//           <div className="flex justify-between text-xs text-slate-600 mt-2">
//             <span>0%</span>
//             <span>20%</span>
//             <span>40%</span>
//             <span>60%</span>
//             <span>80%</span>
//             <span>100%</span>
//           </div>
//           <div className="text-center text-xs text-slate-500 mt-1">
//             Every percentage gets a unique color
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// };

// --- Component: Benchmarking Summary Bar ---
// const BenchmarkingSummary = ({ filters, activeIndication, currentDataset }) => {
//   // Don't show summary for Global CAR-T (it has its own header)
//   if (filters.comparator === 'Global CAR-T') {
//     return null;
//   }

//   return (
//     <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 mb-6">
//       <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
//         {/* Benchmarking Info */}
//         <div className="flex items-center gap-4">
//           <div className="bg-indigo-50 rounded-lg p-3 flex items-center gap-3">
//             <Scale className="w-5 h-5 text-indigo-600" />
//             <div>
//               <div className="text-lg font-bold text-indigo-900">Benchmarking</div>
//               <div className="text-xs text-indigo-600 font-medium">{activeIndication} Analysis</div>
//             </div>
//           </div>
//         </div>

//         {/* Comparator Info */}
//         <div className="flex-1">
//           <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Active Comparison</div>
//           <div className="flex flex-wrap gap-2">
//             <Badge color="indigo">
//               {filters.comparator}
//             </Badge>
//             {filters.comparator === 'SOC' && filters.selectedRegimen && (
//               <Badge color="amber">
//                 {filters.selectedRegimen}
//               </Badge>
//             )}
//             <Badge color="slate">
//               vs Global CAR-T
//             </Badge>
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// };

// // --- Component: Active Filters Summary Bar ---
// const ActiveFiltersSummary = ({ filters, activeModule, totalPatients, apiData }) => {
//   const getActiveFiltersText = () => {
//     const filterTexts = [];

//     // Define display names for levels
//     const levelNames = {
//       'global': 'Overall Cohort',
//       'institute': 'Hospitals',
//       'region': 'Geographic Regions',
//       'physician': 'Physicians'
//     };

//     if (filters.activeLevel !== 'global') {
//       filterTexts.push(`Level: ${levelNames[filters.activeLevel]}`);

//       if (filters.activeLevel === 'region' && filters.selectedRegion) {
//         filterTexts.push(`Region: ${filters.selectedRegion}`);
//       }
//       if (filters.activeLevel === 'institute' && filters.selectedInstitute) {
//         filterTexts.push(`Hospital: ${filters.selectedInstitute}`);
//       }
//       if (filters.activeLevel === 'physician' && filters.selectedPhysician) {
//         filterTexts.push(`Physician: ${filters.selectedPhysician}`);
//       }
//     }

//     if (activeModule === 'benchmarking' && filters.comparator !== 'NexCAR19') {
//       filterTexts.push(`Comparator: ${filters.comparator}`);
//       if (filters.comparator === 'SOC' && filters.selectedRegimen) {
//         filterTexts.push(`Regimen: ${filters.selectedRegimen}`);
//       }
//     }

//     return filterTexts.length > 0 ? filterTexts : ['All Patients (Overall Cohort)'];
//   };

//   // Get the actual filtered patient count from API data if available
//   // IMPORTANT: Always prioritize apiData when available, as it contains the filtered results
//   const actualPatientCount = apiData ? apiData.totalPatients : totalPatients;
//   const isFiltered = filters.activeLevel !== 'global' ||
//     (activeModule === 'benchmarking' && filters.comparator !== 'NexCAR19');

//   // Debug logging for patient count
//   React.useEffect(() => {
//     if (process.env.NODE_ENV === 'development') {
//       console.log('🔍 ActiveFiltersSummary Patient Count Debug:', {
//         'apiData?.totalPatients': apiData?.totalPatients,
//         'totalPatients': totalPatients,
//         'actualPatientCount': actualPatientCount,
//         'isFiltered': isFiltered,
//         'activeLevel': filters.activeLevel,
//         'selectedPhysician': filters.selectedPhysician,
//         'selectedRegion': filters.selectedRegion,
//         'apiData exists': !!apiData
//       });
//     }
//   }, [apiData, totalPatients, actualPatientCount, isFiltered, filters.activeLevel, filters.selectedPhysician, filters.selectedRegion]);

//   return (
//     <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 mb-6">
//       <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
//         {/* Active Filters */}
//         <div className="flex-1">
//           <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Active Filters</div>
//           <div className="flex flex-wrap gap-2">
//             {getActiveFiltersText().map((filter, index) => (
//               <Badge key={index} color={isFiltered ? "amber" : "slate"}>
//                 {filter}
//               </Badge>
//             ))}
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// };

// --- Component: Filter Sidebar ---
const FilterSidebar = ({
  isOpen,
  toggle,
  filters,
  setFilters,
  activeModule,
  activeIndication,
  filtersData,
  maxFollowUpDate,
  rbacConfig,
}) => {
  // --- Helper Functions ---
  const setLevel = (level) => {
    setFilters(prev => ({
      ...prev,
      activeLevel: level,
      // Clear other selections when switching levels
      // For singleHospitalMode physicians switching to institute, auto-select their only hospital
      selectedInstitute: level === 'institute'
        ? (rbacConfig?.singleHospitalMode && rbacConfig?.allowedHospitals?.[0]?.name
            ? rbacConfig.allowedHospitals[0].name
            : prev.selectedInstitute)
        : '',
      selectedRegion: level === 'region' ? prev.selectedRegion : '',
      selectedPhysician: level === 'physician' ? prev.selectedPhysician : ''
    }));
  };

  const handleComparatorChange = (type) => {
    setFilters(prev => ({
      ...prev,
      comparator: type,
      // Reset regimen if switching away from SOC
      selectedRegimen: type === 'SOC' ? (BENCHMARK_REGIMENS[activeIndication]?.[0] || '') : ''
    }));
  };

  // Safe access to regimens to prevent crash if indication mismatch
  // const currentRegimens = BENCHMARK_REGIMENS[activeIndication] || [];

  // Use real data from API or fallback to mock data (deduplicated)
  // Apply RBAC scoping: sales sees only their zones' hospitals, physician sees only their hospitals
  const allInstitutes = [...new Set(filtersData?.hospitals || FALLBACK_FILTERS_DATA.institutes)];
  const allRegions = [...new Set(filtersData?.regions || FALLBACK_FILTERS_DATA.regions)]
    .map(r => r.charAt(0).toUpperCase() + r.slice(1)).sort();
  const allPhysicians = [...new Set(filtersData?.physicians || FALLBACK_FILTERS_DATA.physicians)];

  const currentFiltersData = {
    // Hospitals: scope to allowedHospitals for physician, or filter by allowedZones for sales
    institutes: rbacConfig?.allowedHospitals
      ? rbacConfig.allowedHospitals.map(h => h.name)
      : allInstitutes,

    // Regions: scope to allowedZones for sales
    regions: rbacConfig?.allowedZones
      ? allRegions.filter(r => rbacConfig.allowedZones.includes(r))
      : allRegions,

    physicians: allPhysicians,
  };

  // Check if we're using real data
  // const isUsingRealData = filtersData && filtersData.hospitals && filtersData.hospitals.length > 0;

  // --- Render ---
  // Benchmarking: always open, no collapse
  // Personas: collapsible — collapsed strip when closed, full panel when open
  const isBenchmarking = activeModule === 'benchmarking';
  const effectivelyOpen = isBenchmarking || isOpen;

  return (
    <div
      className={`fixed left-0 top-0 h-screen z-[100] flex transition-all duration-300 ${effectivelyOpen ? 'w-64 md:w-64 lg:w-72' : 'w-10'}`}
    >
      {/* ── Collapsed strip (Personas only, when closed) ── */}
      {!effectivelyOpen && (
        <div
          onClick={toggle}
          className="w-10 h-full bg-white border-r border-slate-200 flex flex-col items-center justify-center cursor-pointer hover:bg-slate-50 transition-colors shadow-sm select-none"
          title="Open Filters"
        >
          {/* Chevron at top */}
          <div className="absolute top-4 flex items-center justify-center w-8 h-8">
            <ChevronDown className="w-4 h-4 text-slate-400 -rotate-90" />
          </div>
          {/* Vertical FILTERS label */}
          <span
            className="text-[11px] font-bold tracking-[0.25em] text-slate-400 uppercase"
            style={{ writingMode: 'vertical-rl', textOrientation: 'mixed', transform: 'rotate(180deg)' }}
          >
            Filters
          </span>
        </div>
      )}

      {/* ── Expanded panel ── */}
      {effectivelyOpen && (
        <div className="w-full bg-white border-r border-slate-200 flex flex-col h-full shadow-lg">
          <div className="p-4 border-b border-slate-100 flex items-center justify-between bg-white z-10">
            <h3 className="font-bold text-slate-800 flex items-center gap-2">
              {activeModule === 'benchmarking' ? <Scale className="w-4 h-4 text-teal-600" /> : <Filter className="w-4 h-4 text-teal-600" />}
              {activeModule === 'benchmarking' ? 'Benchmarks' : 'Filters'}
            </h3>
            {/* Only show collapse button for Personas — Benchmarking is always open */}
            {!isBenchmarking && (
              <button onClick={toggle} className="p-1.5 hover:bg-slate-100 rounded-md text-slate-400 hover:text-slate-600" title="Collapse">
                <ChevronLeft className="w-4 h-4" />
              </button>
            )}
          </div>

      {/* Data Source Indicator - Hidden in Benchmarking mode */}
      {/* {activeModule !== 'benchmarking' && (
        <div className="px-4 py-2 bg-slate-50 border-b border-slate-100">
          <div className="flex items-center gap-2 text-xs">
            <div className={`w-2 h-2 rounded-full ${isUsingRealData ? 'bg-green-500' : 'bg-amber-500'}`}></div>
            <span className="text-slate-600 font-medium">
              {isUsingRealData ? `Real World Cohort: ${filtersData.totalRecords}` : 'Fallback Data'}
            </span>
          </div>
        </div>
      )} */}

      <div className="flex-1 overflow-y-auto p-4 space-y-6">
        {/* --- COMMON: Gradient Scheme Filter --- */}
        {/* <div>
          <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-1">
            <Box className="w-3 h-3" /> Color Scheme
          </label>
          <div className="space-y-2">
            {Object.keys(GRADIENT_CONFIG).map((scheme) => (
              <div
                key={scheme}
                onClick={() => setFilters(prev => ({ ...prev, gradientScheme: scheme }))}
                className={`p-2 rounded-lg cursor-pointer border transition-all ${filters.gradientScheme === scheme ? 'bg-teal-50 border-teal-200 shadow-sm' : 'bg-white border-slate-200 hover:border-slate-300'
                  }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className={`text-sm font-bold capitalize ${filters.gradientScheme === scheme ? 'text-teal-900' : 'text-slate-700'}`}>
                    {scheme}
                  </span>
                  {filters.gradientScheme === scheme && <Check className="w-4 h-4 text-teal-600" />}
                </div>
                <div 
                  className="h-3 rounded border border-slate-200"
                  style={{ 
                    background: (() => {
                      const stops = GRADIENT_CONFIG[scheme].stops;
                      const gradientStops = stops.map(stop => 
                        `rgb(${stop.color[0]}, ${stop.color[1]}, ${stop.color[2]}) ${stop.percent}%`
                      ).join(', ');
                      return `linear-gradient(to right, ${gradientStops})`;
                    })()
                  }}
                />
              </div>
            ))}
          </div>
        </div> */}

        {/* --- Timeline Filter - Only for Patient Personas --- */}
        {activeModule === 'personas' && (
          <>
            {/* NEW: Date Filters — only show label when date filters are visible */}
            {(rbacConfig?.showDateOfDataLock !== false || rbacConfig?.showLastDateOfFollowUp !== false) && (
            <div className="mb-4">
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-1">
                <Calendar className="w-3 h-3" /> Date Filters
              </label>

              <div className="space-y-3">
                {/* Date of Apheresis Filter — superadmin only */}
                {rbacConfig?.showDateOfDataLock !== false && (
                  <div>
                    <label className="text-xs font-medium text-slate-600 mb-1.5 block">Date of Data Lock:</label>
                    <MonthYearPicker
                      value={filters.apheresisCutoff}
                      onChange={(value) => setFilters(prev => ({ ...prev, apheresisCutoff: value }))}
                      minDate="2023-12"
                      maxDate="2026-04"
                      placeholder="Select cutoff date"
                      label="Date of Apheresis"
                    />
                  </div>
                )}

                {/* Last Date of Follow Up Filter — superadmin only */}
                {rbacConfig?.showLastDateOfFollowUp !== false && (
                  <div>
                    <label className="text-xs font-medium text-slate-600 mb-1.5 block">Last Date of Follow Up:</label>

                    {maxFollowUpDate ? (
                      <MonthYearPicker
                        value={filters.followUpCutoff}
                        onChange={(value) => setFilters(prev => ({ ...prev, followUpCutoff: value }))}
                        minDate={filters.apheresisCutoff ? (() => {
                          const [month, year] = filters.apheresisCutoff.split(' ');
                          const monthNum = new Date(Date.parse(month + " 1, 2000")).getMonth() + 1;
                          return `${year}-${String(monthNum).padStart(2, '0')}`;
                        })() : "2023-12"}
                        maxDate={(() => {
                          const parts = maxFollowUpDate.split('-');
                          // return `${parts[2]}-${parts[1].padStart(2, '0')}`;
                          const apiMax = `${parts[2]}-${parts[1].padStart(2, '0')}`;
                          // Cap at April 2026
                          return apiMax > '2026-04' ? '2026-04' : apiMax;
                        })()}
                        placeholder="Select cutoff date"
                        label="Last Date of Follow Up"
                      />
                    ) : (
                      <div className="w-full bg-white border border-slate-300 text-slate-400 text-sm rounded-lg p-2.5 flex items-center gap-2">
                        <span>Loading...</span>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Clear date filters — only when superadmin has changed from defaults */}
              {rbacConfig?.showDateOfDataLock !== false && (filters.apheresisCutoff !== 'December 2025' || filters.followUpCutoff !== 'April 2026') && (
                <button
                  onClick={() => setFilters(prev => ({ ...prev, apheresisCutoff: 'December 2025', followUpCutoff: 'April 2026' }))}
                  className="w-full mt-3 text-xs text-teal-600 hover:text-teal-700 font-medium py-1.5 flex items-center justify-center gap-1 border border-teal-200 rounded-lg hover:bg-teal-50 transition-colors"
                >
                  <X className="w-3 h-3" /> Reset Date Filters
                </button>
              )}
            </div>
            )}

            {/* Evaluation Timepoint */}
            <div>
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-1">
                <Calendar className="w-3 h-3" /> Evaluation Timepoint
              </label>
              <div className="relative">
                <select
                  value={filters.timeline}
                  onChange={(e) => setFilters(prev => ({ ...prev, timeline: e.target.value }))}
                  className="w-full bg-slate-50 border border-slate-200 text-slate-700 text-sm rounded-lg focus:ring-teal-500 focus:border-teal-500 p-2.5 appearance-none cursor-pointer"
                >
                  <option value="1 Month">1 Month</option>
                  <option value="3 Month">3 Months</option>
                  <option value="6 Month">6 Months</option>
                  <option value="9 Month">9 Months</option>
                  <option value="12 Month">12 Months</option>
                  {/* 18 months only visible to superadmin */}
                  {rbacConfig?.maxTimepointMonths === null && (
                    <option value="18 Month">18 Months</option>
                  )}
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-slate-500">
                  <ChevronDown className="h-4 w-4" />
                </div>
              </div>
            </div>
          </>
        )}

        {/* --- MODULE SPECIFIC --- */}
        {activeModule === 'personas' ? (
          /* PERSONAS FILTERS */
          <div>
            <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3 block">Analysis Level</label>
            <div className="space-y-3">
              {['global', 'region', 'institute', 'physician', 'my_patients'].map(lvl => {
                const levelNames = {
                  'global': 'Overall Evaluable Cohort',
                  'institute': rbacConfig?.role === 'physician' ? 'My Hospitals' : 'Hospitals',
                  'region': 'Geographic Regions',
                  'physician': 'Physicians',
                  'my_patients': 'My Patients',
                };

                // RBAC: hide levels based on role
                if (lvl === 'region' && rbacConfig?.showGeographicRegions === false) return null;
                if (lvl === 'physician' && rbacConfig?.showPhysicians === false) return null;
                if (lvl === 'institute' && rbacConfig?.showHospitals === false) return null;
                // My Patients — only visible to physician role
                if (lvl === 'my_patients' && rbacConfig?.role !== 'physician') return null;

                return (
                  <div key={lvl}>
                    <div
                      onClick={() => setLevel(lvl)}
                      className={`flex items-center p-2 rounded-lg cursor-pointer transition-colors ${filters.activeLevel === lvl ? 'bg-teal-50 border border-teal-100' : 'hover:bg-slate-50 border border-transparent'
                        }`}
                    >
                      {filters.activeLevel === lvl ? <CircleDot className="w-4 h-4 text-teal-600" /> : <Circle className="w-4 h-4 text-slate-300" />}
                      <span className={`ml-2 text-sm font-medium ${filters.activeLevel === lvl ? 'text-teal-900' : 'text-slate-700'
                        } flex items-center gap-2`}>
                        {levelNames[lvl]}
                      </span>
                    </div>
                    {/* Sub-menus */}
                    {filters.activeLevel === lvl && lvl !== 'global' && (
                      <div className="ml-6 mt-2 pl-2 border-l-2 border-teal-100 space-y-1.5 animate-in slide-in-from-left-2 duration-200">
                        {lvl === 'region' && (
                          <div className="relative">
                            <select
                              value={filters.selectedRegion || ''}
                              onChange={(e) => setFilters(prev => ({ ...prev, selectedRegion: e.target.value }))}
                              className="w-full bg-slate-50 border border-slate-200 text-slate-700 text-xs rounded-lg focus:ring-teal-500 focus:border-teal-500 p-2 appearance-none"
                            >
                              <option value="">Select a region...</option>
                              {currentFiltersData.regions.map(r => (
                                <option key={r} value={r}>{r}</option>
                              ))}
                            </select>
                            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-slate-500">
                              <ChevronDown className="h-3 w-3" />
                            </div>
                          </div>
                        )}
                        {lvl === 'institute' && (
                          /* Physician with 1 hospital — show as label, not dropdown */
                          rbacConfig?.singleHospitalMode ? (
                            <div className="px-3 py-2 bg-teal-50 border border-teal-100 rounded-lg text-xs font-medium text-teal-700">
                              {formatHospitalDisplay(currentFiltersData.institutes[0])}
                            </div>
                          ) : (
                            <SearchableInstituteDropdown
                              institutes={currentFiltersData.institutes}
                              selectedInstitute={filters.selectedInstitute}
                              onSelect={(institute) => setFilters(prev => ({ ...prev, selectedInstitute: institute }))}
                              placeholder="Search hospitals..."
                            />
                          )
                        )}
                        {lvl === 'physician' && (
                          <SearchablePhysicianDropdown
                            physicians={currentFiltersData.physicians}
                            selectedPhysician={filters.selectedPhysician}
                            onSelect={(physician) => setFilters(prev => ({ ...prev, selectedPhysician: physician }))}
                            placeholder="Search physicians..."
                          />
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          /* BENCHMARKING FILTERS */
          <div className="space-y-6">
            <div>
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3 block">Comparators</label>
              <div className="space-y-2">
                {[
                  { label: 'Global CAR-T', desc: 'International Approved Products' },
                  { label: 'SOC', desc: 'Standard of Care Protocols' },
                ].map((opt) => (
                  <div
                    key={opt.label}
                    onClick={() => handleComparatorChange(opt.label)}
                    className={`p-3 rounded-lg cursor-pointer border transition-all ${filters.comparator === opt.label
                        ? 'bg-indigo-50 border-indigo-200 shadow-sm'
                        : 'bg-white border-slate-200 hover:border-slate-300'
                      }`}
                  >
                    <div className="flex items-center justify-between">
                      <span
                        className={`text-sm font-bold ${filters.comparator === opt.label ? 'text-indigo-900' : 'text-slate-700'
                          }`}
                      >
                        {opt.label}
                      </span>
                    </div>
                    <div className="text-xs text-slate-500 mt-1">{opt.desc}</div>
                  </div>
                ))}
              </div>
            </div>

          </div>
        )}

      </div>

      {/* Data Updated Info - Fixed at Bottom — hidden in Benchmarking mode */}
      {maxFollowUpDate && activeModule !== 'benchmarking' && (
        <div className="p-3 border-t border-slate-200 bg-white">
          <p className="text-sm text-slate-600">
            Data on file updated on: <span className='font-bold'>17-04-2026</span>
          </p>
          <p className="text-sm text-slate-600 mt-1">
            Patient Profile updated on: <span className='font-bold'>15-06-2026</span>
          </p>
        </div>
      )}
        </div>
      )}
    </div>
  );
};

// --- Module: Patient Personas NHL View ---
const PersonasNHLView = ({
  timeline,
  dataset,
  comparisonDataset,
  onCardClick,
  analysisType,
  gradientScheme,
  selectedPersona = null,
  onResetToOverall = null,
  activeIndication = 'NHL',
  selectedCard = null,
  linesOfFailureData = null,
  onLinesDataLoaded = null,
  filters = null,
  safetyData = null,
  onHospitalDataReady = null,
  hideHospitalDistribution = false,
  onStatsReady = null,
  userRole = null,
  preloadedOverallLinesData = null,
  onShowPreviousData = null,
}) => {
  // DEBUG: Log dataset structure (only in development)
  if (process.env.NODE_ENV === 'development') {
    console.log('📊 PersonasNHLView received dataset:', {
      'dataset exists': !!dataset,
      'dataset.overallCohort exists': !!dataset?.overallCohort,
      'dataset.overallCohort.kmData exists': !!dataset?.overallCohort?.kmData,
      'dataset.overallCohort.kmData has at_risk': dataset?.overallCohort?.kmData ? !!dataset.overallCohort.kmData.at_risk : 'N/A'
    });
  }

  // Calculate patient count for the card
  const totalPatients = dataset.totalPatients;

  return (
    <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
      {/* Overall Cohort Card */}
      {/* <OverallCohortCard
        dataset={dataset}
        analysisType={analysisType}
        gradientScheme={gradientScheme}
        onCardClick={onCardClick}
        indication="NHL"
      /> */}

      {/* Individual Subgroups */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 px-3 lg:px-4 lg:py-3 mb-4 relative z-10">
        <div className="mb-2">
          <div className="flex items-center gap-2 mb-1 justify-between">
            <h3 className="text-base font-bold text-slate-800 tracking-wide">Patient Persona Classification <GlossaryLink section="grid" tooltip={`Every EE patient is classified into a subgroup (cell) based on baseline characteristics. Each cell reports subgroup-level ${analysisType} at ${timeline.replace(' Month', ' months')}, along with the incidence of Grade ≥ 3 CRS and Grade ≥ 3 ICANS within the subgroup.`} /></h3>
            {onShowPreviousData && (
              <button
                onClick={onShowPreviousData}
                className="font-normal text-slate-400 font-mono bg-slate-100 px-2 py-1 rounded-md text-[11px] hover:bg-slate-200 hover:text-slate-500 transition-colors"
              >
                Previous Data
              </button>
            )}
          </div>

          {filters?.activeLevel === 'institute' && filters?.selectedInstitute && (
            <p className="mt-2 text-[13px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 flex items-start gap-1.5">
              <span>
                <strong>Hospital view:</strong> Patient count reflects all patients treated at <strong>{filters.selectedInstitute}</strong> across all physicians.{userRole === 'physician' && <> To view only your patients, use <strong>My Patients</strong>.</>}
              </span>
            </p>
          )}
        </div>

        <div className="w-full md:overflow-x-auto lg:overflow-x-visible">
          {/* Header Grid - Full Width */}
          <div className="grid grid-cols-10 gap-2 lg:gap-1 mb-2 md:min-w-[640px] lg:min-w-0">
            <div className="col-span-2">
              <div className="bg-transparent rounded-lg py-1 px-2 flex flex-col items-center justify-center h-11">
                {/* <div className="flex items-baseline gap-0.5 lg:gap-1 h-7">
                  <Users className="w-4 h-4 text-teal-900 flex-shrink-0" />
                  <span className='text-lg lg:text-2xl font-bold text-teal-900 leading-none'>
                    {dataset?.overallCohort?.n1 !== undefined ? dataset.overallCohort.n1 : totalPatients}
                  </span>
                  {dataset?.overallCohort?.n1 !== undefined && (
                    <>
                      <span className="text-base lg:text-xl font-bold text-teal-700 leading-none">/</span>
                      <span className="text-base lg:text-xl font-bold text-teal-700 leading-none">{totalPatients}</span>
                    </>
                  )}
                </div>
                <span className='text-xs lg:text-sm font-medium text-teal-600 ml-0.5 lg:ml-1 whitespace-nowrap'>Patients</span> */}
              </div>
            </div>
            <div className="col-span-4 flex flex-col items-center justify-center bg-teal-50 rounded-t-lg border-t border-x border-teal-100 py-1 text-center relative group">
              <div className="absolute top-0 left-0 w-full h-1 bg-teal-500 rounded-t-lg"></div>
              <div className="flex justify-center items-center gap-1.5 text-teal-800 font-bold uppercase tracking-wider text-xs lg:text-sm">
                Non-Bulky
                <span className="font-normal normal-case text-teal-600 text-[10px] lg:text-xs">(Tumor Max Dim &lt; 5cm)</span>
              </div>
            </div>
            <div className="col-span-4 flex flex-col items-center justify-center bg-indigo-50 rounded-t-lg border-t border-x border-indigo-100 py-1 text-center relative group">
              <div className="absolute top-0 left-0 w-full h-1 bg-indigo-500 rounded-t-lg"></div>
              <div className="flex justify-center items-center gap-1.5 text-indigo-800 font-bold uppercase tracking-wider text-xs lg:text-sm">
                Bulky
                <span className="font-normal normal-case text-indigo-600 text-[10px] lg:text-xs">(Tumor Max Dim ≥ 5cm)</span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-10 gap-2 lg:gap-1 md:min-w-[640px] lg:min-w-0">
            <div className="col-span-2 flex items-end pb-2 pl-1 lg:pl-2">
              <span className="text-[10px] lg:text-xs font-bold text-slate-400 uppercase tracking-widest"></span>
            </div>
            {dataset.columns.map((col, index) => (
              <div key={col.id} className="col-span-2 text-center">
                <div className="flex justify-center items-center gap-1.5 text-xs lg:text-sm font-bold text-slate-700">
                  {col.label}
                  <span className="font-normal text-slate-400 font-mono bg-slate-100 px-1 lg:px-1.5 rounded text-[8px] lg:text-[10px]">IPI {col.range}</span>
                </div>
              </div>
            ))}
          </div>

          {/* No Data Banner - Show when totalPatients is 0 */}
          {totalPatients === 0 && (
            <div className="mb-6 bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3 mt-4">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z" />
              </svg>
              <div>
                <div className="font-semibold text-amber-900 mb-1">No Patient Data Available</div>
                <div className="text-sm text-amber-700">
                  {userRole === 'physician' && (filters?.activeLevel === 'my_patients' || filters?.activeLevel === 'global')
                    ? 'No patients available for the selected physician.'
                    : 'There are currently no patients matching the selected filters and criteria. Please adjust your filters (physician, indication, timeline) to view patient subgroup data.'
                  }
                </div>
              </div>
            </div>
          )}

          {/* Rows - Full Width Grid */}
          <div className="space-y-3 lg:space-y-0">
            {dataset.rows.map((row, rIdx) => (
              <div key={row.id} className="grid grid-cols-10 gap-2 lg:gap-1 items-stretch group hover:bg-slate-50/50 rounded-xl transition-colors p-1 lg:p-2 -mx-1 lg:-mx-2 md:min-w-[640px] lg:min-w-0">
                <div className="col-span-2 flex flex-col justify-center pr-2 lg:pr-4 border-r border-slate-100 relative">
                  <h3 className="font-bold text-slate-800 text-sm lg:text-[15px] leading-tight">{row.title}</h3>
                  <p className="text-[10px] lg:text-xs text-slate-500 mt-1 line-clamp-2">{row.desc}</p>
                  <div className="text-xs text-slate-400 mt-2 font-mono flex items-center gap-1">
                    <Users className="w-3 h-3" />n = {row.totalN}
                  </div>
                </div>
                {dataset.columns.map((col, colIndex) => {
                  const currentValue = analysisType.toLowerCase() === 'pfs' ? row.values[col.id].pfs : row.values[col.id].os;
                  const compData = comparisonDataset ? comparisonDataset.rows[rIdx].values[col.id] : null;
                  // Background color: percentage-based gradient (unchanged)
                  const cardColor = getContinuousGradientColor(currentValue, gradientScheme);
                  // OLD: contrast text color based on percentage (kept for future use)
                  // const textColor = getContrastTextColor(currentValue);
                  // NEW: always black text per client requirement
                  const textColor = 'text-black';

                  // Check if this card is selected
                  const isSelected = selectedCard && selectedCard.rowId === row.id && selectedCard.colId === col.id;

                  // Check if card should be disabled (0% with 0 patients)
                  const isDisabled = currentValue === 0 && row.values[col.id].n === 0;
                  // Check if card is "None At Risk" (patients exist but none at risk)
                  // BUT exclude genuine zeros — when all patients had events (genuine 0% survival)
                  const isNoneAtRisk = (currentValue === 0 && row.values[col.id].n > 0 && !row.values[col.id].genuineZero) || row.values[col.id].forceHourglass;

                  // Check if patient count is less than 10
                  const isLowPatientCount = row.values[col.id].n < 10 && row.values[col.id].n > 0;

                  // Build persona label to look up safety bucket (NHL format)
                  const bulkyPart = col.sub === 'Non-Bulky' ? 'Non-Bulky' : 'Bulky';
                  const ipiPart = col.range === '0-2' ? 'IPI Low (0-2)' : 'IPI High (3-5)';
                  const nhlSafetyLabel = `${row.title} | ${bulkyPart} | ${ipiPart}`;
                  const safetyBucket = safetyData?.persona_buckets?.find(b => b.label === nhlSafetyLabel);

                  return (
                    <div key={`${row.id}-${col.id}`} className="col-span-2">
                      <div
                        className={` rounded-lg p-2 lg:p-3 flex flex-col justify-between transition-all ${isDisabled
                          ? 'cursor-not-allowed opacity-40 grayscale'
                          : 'hover:scale-[1.02] hover:shadow-lg cursor-pointer'
                          } ${isNoneAtRisk ? 'opacity-100 grayscale' : ''} ${textColor} relative overflow-hidden ${isSelected
                            ? 'border-4 border-black/40 shadow-xl'
                            : 'border border-white'
                          }`}
                        style={{
                          backgroundColor: isDisabled || isNoneAtRisk ? '#e2e8f0' : cardColor
                        }}
                        onClick={() => !isDisabled && onCardClick && onCardClick(row.values[col.id], row, col)}
                      >
                        {/* Subtle gradient overlay for depth */}
                        <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-black/5 pointer-events-none" />

                        <div className="flex justify-end items-end z-10">
                          {!compData && (
                            <div className="flex items-center gap-1 opacity-90 bg-black/15 px-1 lg:px-1.5 py-0.5 rounded text-[10px] lg:text-[12px] backdrop-blur-sm">
                              <Users className="w-2 h-2 lg:w-3 lg:h-3" />
                              <span className="font-mono">{row.values[col.id].n}{isLowPatientCount && '*'}</span>
                            </div>
                          )}
                        </div>
                        <div className="flex flex-col items-start z-10">
                          <div className="text-xl lg:text-4xl font-bold tracking-tight leading-none drop-shadow-sm">
                            {isDisabled
                              ? <span className="text-sm lg:text-3xl font-medium opacity-80">-</span>
                              : isNoneAtRisk
                                ? <span className="inline-flex items-center justify-center h-[1em]">
                                    <HourglassTooltip />
                                  </span>
                                : <>{currentValue}<span className="text-sm lg:text-lg font-medium opacity-80">%</span></>
                            }
                          </div>
                          {/* Visual Bar with gradient */}
                          {!compData && (
                            <div className="w-full h-0.5 lg:h-1 bg-black/10 rounded-full mt-1 lg:mt-1 overflow-hidden">
                              <div
                                className="h-full opacity-60 rounded-full"
                                style={{
                                  width: `${currentValue}%`,
                                  backgroundColor: 'currentColor'
                                }}
                              />
                            </div>
                          )}
                          {/* Safety Profile: CRS & ICANs Grade 3+ */}
                          {!compData && safetyBucket && (
                            <div className="w-full flex justify-between mt-1 lg:mt-1.5">
                              <span className="text-[8px] lg:text-[10px] font-medium opacity-80">
                                CRS Gr ≥3: {isDisabled ? 0 : (safetyBucket.crs?.grade3_plus_count ?? 0)}
                              </span>
                              <span className="text-[8px] lg:text-[10px] font-medium opacity-80">
                                ICANS Gr ≥3: {isDisabled ? 0 : (safetyBucket.icans?.grade3_plus_count ?? 0)}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>

          {/* Asterisk Description */}
          <div className="mt-4 px-3 py-3 bg-amber-50 border border-amber-200 rounded-lg">
            <p className="text-xs text-slate-700">
              <span className="font-bold">Note:</span> Results with n ≤ 10 (marked with *) may be statistically immature and should be interpreted with caution. The persona classification is done based on data on file updated on 17-04-2026.
            </p>
          </div>
        </div>
      </div>

      {/* Overall Cohort — Lines of Therapy + Safety Profile + KM Curve + Risk Table + Hospital Distribution */}
      <OverallCohortChart
        indication={activeIndication}
        analysisType={analysisType}
        timeline={timeline}
        gradientScheme={gradientScheme}
        overallCohortData={dataset?.overallCohort || null}
        selectedPersonaData={null}
        linesOfFailureData={preloadedOverallLinesData}
        onResetToOverall={null}
        filters={filters}
        selectedCard={selectedCard}
        onLinesDataLoaded={onLinesDataLoaded}
        onHospitalDataReady={onHospitalDataReady}
        hideHospitalDistribution={hideHospitalDistribution}
        overallSafetyData={safetyData?.overall || null}
        onStatsReady={onStatsReady}
        userRole={userRole}
        preloadedOverallLinesData={preloadedOverallLinesData}
      />


    </div>
  );
};

// --- Module: Patient Personas B-ALL View ---
const PersonasBALLView = ({
  timeline,
  dataset,
  comparisonDataset,
  onCardClick,
  analysisType,
  gradientScheme,
  selectedPersona = null,
  onResetToOverall = null,
  activeIndication = 'B-ALL',
  selectedCard = null,
  linesOfFailureData = null,
  onLinesDataLoaded = null,
  filters = null,
  safetyData = null,
  onHospitalDataReady = null,
  hideHospitalDistribution = false,
  onStatsReady = null,
  userRole = null,
  preloadedOverallLinesData = null,
  onShowPreviousData = null,
}) => {
  // Calculate patient count for the card
  const totalPatients = dataset.totalPatients;

  return (
    <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
      {/* Individual Subgroups */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-3 lg:p-4 mb-4 relative z-10">
        <div className="mb-2">
          <div className="flex items-center gap-2 mb-1 justify-between">
            <h3 className="text-base font-bold text-slate-800 tracking-wide">Patient Persona Classification <GlossaryLink section="grid" tooltip={`Every EE patient is classified into a subgroup (cell) based on baseline characteristics. Each cell reports subgroup-level ${analysisType} at ${timeline.replace(' Month', ' months')}, along with the incidence of Grade ≥ 3 CRS and Grade ≥ 3 ICANS within the subgroup.`} /></h3>
            {onShowPreviousData && (
              <button
                onClick={onShowPreviousData}
                className="font-normal text-slate-400 font-mono bg-slate-100 px-2 py-1 rounded-md text-[11px] hover:bg-slate-200 hover:text-slate-500 transition-colors"
              >
                Previous Data
              </button>
            )}
          </div>

          {filters?.activeLevel === 'institute' && filters?.selectedInstitute && (
            <p className="mt-2 text-[13px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 flex items-start gap-1.5">
              <span>
                <strong>Hospital view:</strong> Patient count reflects all patients treated at <strong>{filters.selectedInstitute}</strong> across all physicians.{userRole === 'physician' && <> To view only your patients, use <strong>My Patients</strong>.</>}
              </span>
            </p>
          )}
        </div>

        <div className="w-full md:overflow-x-auto lg:overflow-x-visible">
          {/* Header Grid - 3 equal columns: MRD -ve, MRD +ve, Blast >=5% */}
          <div className="grid grid-cols-11 gap-2 lg:gap-1 mb-2 md:min-w-[700px] lg:min-w-0">
            <div className="col-span-2">
              <div className="bg-transparent h-11 rounded-lg py-1 px-2 lg:px-3 flex items-center justify-center">
              </div>
            </div>
            <div className="col-span-3 flex flex-col items-center justify-center bg-teal-50 rounded-t-lg border-t border-x border-teal-100 py-1 px-2 text-center relative">
              <div className="absolute top-0 left-0 w-full h-1 bg-teal-500 rounded-t-lg"></div>
              <div className="flex justify-center items-center gap-1">
                <span className="text-teal-800 font-bold uppercase text-xs lg:text-sm">MRD -ve</span>
              </div>
            </div>
            <div className="col-span-3 flex flex-col items-center justify-center bg-amber-50 rounded-t-lg border-t border-x border-amber-100 py-1 px-2 text-center relative">
              <div className="absolute top-0 left-0 w-full h-1 bg-amber-500 rounded-t-lg"></div>
              <div className="flex justify-center items-center gap-1.5">
                <span className="text-amber-800 font-bold uppercase text-xs lg:text-sm">MRD +ve</span>
              </div>
            </div>
            <div className="col-span-3 flex flex-col items-center justify-center bg-rose-50 rounded-t-lg border-t border-x border-rose-100 py-1 px-2 text-center relative">
              <div className="absolute top-0 left-0 w-full h-1 bg-rose-500 rounded-t-lg"></div>
              <div className="flex justify-center items-center gap-1">
                <span className="text-rose-800 font-bold uppercase text-xs lg:text-sm">Blast ≥5%</span>
              </div>
            </div>
          </div>

          {/* No Data Banner - Show when totalPatients is 0 */}
          {totalPatients === 0 && (
            <div className="mb-6 bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3 mt-4">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z" />
              </svg>
              <div>
                <div className="font-semibold text-amber-900 mb-1">No Patient Data Available</div>
                <div className="text-sm text-amber-700">
                  {userRole === 'physician' && (filters?.activeLevel === 'my_patients' || filters?.activeLevel === 'global')
                    ? 'No patients available for the selected physician.'
                    : 'There are currently no patients matching the selected filters and criteria. Please adjust your filters (physician, indication, timeline) to view patient subgroup data.'
                  }
                </div>
              </div>
            </div>
          )}

          {/* Rows - Full Width Grid (4 rows × 3 equal columns) */}
          <div className="space-y-3 lg:space-y-0 mt-4 md:overflow-x-auto lg:overflow-x-visible">
            {dataset.rows.map((row, rIdx) => (
              <div key={row.id} className="grid grid-cols-11 gap-2 lg:gap-1 items-stretch group hover:bg-slate-50/50 rounded-xl transition-colors p-1 lg:p-2 -mx-1 lg:-mx-2 md:min-w-[700px] lg:min-w-0">
                <div className="col-span-2 flex flex-col justify-center pr-2 lg:pr-4 border-r border-slate-100">
                  <h3 className="font-bold text-slate-800 text-sm lg:text-[15px] leading-tight">{row.title}</h3>
                  <p className="text-[10px] lg:text-xs text-slate-500 mt-0.5">{row.linesLabel} <span className="text-slate-400">/ {row.desc}</span></p>
                  <div className="text-xs text-slate-400 mt-1.5 font-mono flex items-center gap-1">
                    <Users className="w-3 h-3" />n = {row.totalN}
                  </div>
                </div>
                {dataset.columns.map(col => {
                  const currentValue = analysisType.toLowerCase() === 'pfs' ? row.values[col.id].pfs : row.values[col.id].os;
                  const compData = comparisonDataset ? comparisonDataset.rows[rIdx]?.values[col.id] : null;
                  const cardColor = getContinuousGradientColor(currentValue, gradientScheme);
                  const textColor = 'text-black';

                  const isSelected = selectedCard && selectedCard.rowId === row.id && selectedCard.colId === col.id;
                  const isDisabled = currentValue === 0 && row.values[col.id].n === 0;
                  const isNoneAtRisk = (currentValue === 0 && row.values[col.id].n > 0 && !row.values[col.id].genuineZero) || row.values[col.id].forceHourglass;
                  const isLowPatientCount = row.values[col.id].n < 10 && row.values[col.id].n > 0;

                  // Build persona label to look up safety bucket (new MRD format)
                  const allSafetyLabel = `${row.title} | ${row.linesLabel} | ${col.sub}`;
                  const safetyBucket = safetyData?.persona_buckets?.find(b => b.label === allSafetyLabel);

                  return (
                    <div key={`${row.id}-${col.id}`} className="col-span-3">
                      <div
                        className={`min-h-[5rem] lg:min-h-[7rem] rounded-lg p-2 lg:p-3 flex flex-col justify-between transition-all ${isDisabled
                          ? 'cursor-not-allowed opacity-40 grayscale'
                          : 'hover:shadow-lg cursor-pointer'
                          } ${isNoneAtRisk ? 'opacity-100 grayscale' : ''} ${textColor} relative overflow-hidden ${isSelected
                            ? 'border-4 border-black/40 shadow-xl'
                            : 'border border-white/20'
                          }`}
                        style={{ backgroundColor: isDisabled || isNoneAtRisk ? '#e2e8f0' : cardColor }}
                        onClick={() => !isDisabled && onCardClick && onCardClick(row.values[col.id], row, col)}
                      >
                        <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-black/5 pointer-events-none" />

                        <div className="flex justify-end items-start z-10">
                          {!compData && (
                            <div className="flex items-center gap-1.5 opacity-95 bg-black/15 px-2 lg:px-3 py-1 lg:py-1.5 rounded-lg text-xs lg:text-sm font-bold backdrop-blur-sm">
                              <Users className="w-3 h-3 lg:w-4 lg:h-4" />
                              <span className="font-mono">{row.values[col.id].n}{isLowPatientCount && '*'}</span>
                            </div>
                          )}
                        </div>
                        <div className="flex flex-col items-start z-10 mt-auto">
                          <div className="text-3xl lg:text-5xl font-bold tracking-tight leading-none drop-shadow-sm">
                            {isDisabled
                              ? <span className="text-lg lg:text-3xl font-bold opacity-80">-</span>
                              : isNoneAtRisk
                                ? <span className="inline-flex items-center justify-center h-[1em]">
                                    <HourglassTooltip />
                                  </span>
                                : <>{currentValue}<span className="text-xl lg:text-2xl font-medium opacity-80">%</span></>
                            }
                          </div>
                          {/* Visual Bar with gradient */}
                          {!compData && (
                            <div className="w-full h-0.5 lg:h-1 bg-black/10 rounded-full mt-1 lg:mt-1 overflow-hidden">
                              <div
                                className="h-full opacity-60 rounded-full"
                                style={{
                                  width: `${currentValue}%`,
                                  backgroundColor: 'currentColor'
                                }}
                              />
                            </div>
                          )}
                          {/* Safety Profile: CRS & ICANs Grade 3+ */}
                          {!compData && safetyBucket && (
                            <div className="w-full flex justify-between mt-1 lg:mt-1.5">
                              <span className="text-[8px] lg:text-[10px] font-medium opacity-80">
                                CRS Gr ≥3: {isDisabled ? 0 : (safetyBucket.crs?.grade3_plus_count ?? 0)}
                              </span>
                              <span className="text-[8px] lg:text-[10px] font-medium opacity-80">
                                ICANS Gr ≥3: {isDisabled ? 0 : (safetyBucket.icans?.grade3_plus_count ?? 0)}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>

          {/* Asterisk Description */}
          <div className="mt-4 px-3 py-3 bg-amber-50 border border-amber-200 rounded-lg">
            <p className="text-xs text-slate-700">
              <span className="font-bold">Note:</span> Results with n ≤ 10 (marked with *) may be statistically immature and should be interpreted with caution. The persona classification is done based on data on file updated on 17-04-2026.
            </p>
          </div>
        </div>
      </div>

      {/* Overall Cohort — Lines of Therapy + Safety Profile + KM Curve + Risk Table + Hospital Distribution */}
      <OverallCohortChart
        indication={activeIndication}
        analysisType={analysisType}
        timeline={timeline}
        gradientScheme={gradientScheme}
        overallCohortData={dataset?.overallCohort || null}
        selectedPersonaData={null}
        linesOfFailureData={preloadedOverallLinesData}
        onResetToOverall={null}
        filters={filters}
        selectedCard={selectedCard}
        onLinesDataLoaded={onLinesDataLoaded}
        onHospitalDataReady={onHospitalDataReady}
        hideHospitalDistribution={hideHospitalDistribution}
        overallSafetyData={safetyData?.overall || null}
        onStatsReady={onStatsReady}
        userRole={userRole}
        preloadedOverallLinesData={preloadedOverallLinesData}
      />


    </div>
  );
};

// ─── Summary Stats Bar ────────────────────────────────────────────────────────
// Shown below the active filter chips, above the persona grid.
// Data is bubbled up from OverallCohortChart via onStatsReady callback.
const SummaryStatsBar = ({ stats, dataset }) => {
  // Show as soon as we have dataset and stats — don't block on ORR
  if (!stats || !dataset) return null;

  const totalPatients = dataset?.overallCohort?.totalPatients ?? 0;
  const n1            = dataset?.overallCohort?.n1 ?? totalPatients;
  const fullDataPct   = totalPatients > 0 ? Math.round((n1 / totalPatients) * 100) : 0;

  const cards = [
    {
      label: 'EFFICACY EVALUABLE COUNT',
      value: totalPatients > 0 ? String(totalPatients) : '—',
      sub: null,
      border: 'border-sky-500',
      textColor: 'text-[#0f172a]',
      tooltip: 'Efficacy-Evaluable (EE) cohort comprising infused patients who have reached Day 28 (D28) post-infusion with an available D28 assessment.',
    },
    {
      label: 'CLASSIFIED COUNTS',
      value: totalPatients > 0 ? `${n1} (${fullDataPct}%)` : '—',
      sub: null,
      border: 'border-indigo-500',
      textColor: 'text-[#0f172a]',
      tooltip: 'EE patients mapped to the patient-persona grid, contingent on availability of required baseline data.',
    },
        {
      label: '28-Day ORR',
      value: stats?.orrData?.orr_percent != null
        ? `${Math.round(stats.orrData.orr_percent)}%`
        : stats?.orrData === undefined
          ? '—'  // still loading
          : '—',
      border: 'border-purple-500',
      textColor: 'text-purple-800',
      loading: stats?.orrData === undefined, // ORR still loading
      tooltip: 'Percentage of EE patients achieving partial response (PR) or complete response (CR) at Day 28 post-infusion.',
    },
    {
      label: `${stats?.timelineMonths ?? 12}-Month ${stats?.analysisType ?? 'PFS'}`,
      value: stats?.survivalAtTimeline != null && !isNaN(stats.survivalAtTimeline)
        ? `${stats.survivalAtTimeline}%`
        : '—',
      sub: '',
      border: 'border-teal-500',
      textColor: 'text-teal-800',
      tooltip: (stats?.analysisType ?? 'PFS') === 'PFS'
        ? `Percentage of patients alive and progression-free at ${stats?.timelineMonths ?? 12} months post-infusion with NexCAR19, estimated by Kaplan-Meier analysis.`
        : `Percentage of patients alive at ${stats?.timelineMonths ?? 12} months post-infusion with NexCAR19, estimated by Kaplan-Meier analysis.`,
    },
    {
      label: `Median ${stats?.analysisType ?? 'PFS'}`,
      value: stats?.medianTime != null && isFinite(stats.medianTime)
        ? `${Math.round(stats.medianTime)} M`
        : stats?.medianTime === null && !stats?.isEmpty
          ? 'Timepoint Not Reached'
          : '—',
      sub: '',
      border: 'border-amber-500',
      textColor: 'text-amber-800',
      tooltip: (stats?.analysisType ?? 'PFS') === 'PFS'
        ? 'Time (in months) at which 50% of patients are alive and progression-free and the remaining 50% of patients have either progressed or deceased.'
        : 'Time (in months) at which 50% of patients are alive, and the remaining 50% are deceased.',
    },
  ];

  // Unique key based on values — re-triggers animation when data changes
  const animKey = cards.map(c => c.value).join('|');

  return (
    <div className="px-5 py-3 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-x-6 gap-y-3" style={{ overflow: 'visible' }}>
      {cards.map((card, idx) => (
        <div key={idx} className="flex flex-col" style={{ overflow: 'visible' }}>
          <span className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.12em] leading-tight flex items-center gap-1">
            {card.label}
            {card.tooltip && (
              <span className="relative group/tip cursor-help inline-flex">
                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-slate-400 opacity-70">
                  <circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/>
                </svg>
                <span className={`absolute top-full mt-1 hidden group-hover/tip:block z-[99999] w-52 px-3 py-2 rounded-lg bg-slate-800 text-white text-[12px] leading-snug font-normal shadow-xl whitespace-normal text-left pointer-events-none normal-case tracking-normal ${idx >= cards.length - 1 ? 'right-0' : 'left-0'}`}>
                  {card.tooltip}
                </span>
              </span>
            )}
          </span>
          <span
            key={`${animKey}-${idx}`}
            className="text-[18px] font-black text-white leading-tight tracking-tight count-animate"
            style={{ animation: `countFadeUp 0.5s ease-out ${idx * 0.08}s both` }}
          >
            {card.value}
            {card.sub && (
              <span className="text-[11px] font-bold text-slate-300 ml-1.5">{card.sub}</span>
            )}
          </span>
        </div>
      ))}
    </div>
  );
};

// --- Module: Old Data Modal Content (toggle-based single image view) ---
const OldDataModalContent = ({ initialIndication = 'NHL', initialAnalysisType = 'PFS' }) => {
  const [indication, setIndication] = useState(initialIndication === 'B-ALL' ? 'ALL' : 'NHL');
  const [analysisType, setAnalysisType] = useState(initialAnalysisType);

  const imageMap = {
    'NHL-PFS': '/old-data/B-NHL_PFS.png',
    'NHL-OS': '/old-data/B-NHL_OS.png',
    'ALL-PFS': '/old-data/B-ALL_PFS.png',
    'ALL-OS': '/old-data/B-ALL_OS.png',
  };

  const currentImage = imageMap[`${indication}-${analysisType}`];
  const currentLabel = `${indication === 'NHL' ? 'NHL' : 'B-ALL'} — ${analysisType}`;

  return (
    <div className="flex flex-col flex-1 overflow-hidden">
      {/* Toggle bar */}
      <div className="flex items-center justify-center gap-4 px-6 py-3 border-b border-gray-100 bg-gray-50 flex-shrink-0">
        {/* Indication toggle */}
        <div className="flex bg-[#1e293b] rounded-[6px] p-0.5 w-fit">
          {['NHL', 'ALL'].map(opt => (
            <button key={opt} onClick={() => setIndication(opt)}
              className={`px-3.5 py-1 rounded-[6px] text-[10px] font-bold tracking-wide transition-all ${
                indication === opt
                  ? 'bg-white text-[#1e293b] shadow'
                  : 'text-slate-300 hover:text-white'
              }`}>
              {opt === 'ALL' ? 'B-ALL' : opt}
            </button>
          ))}
        </div>
        {/* Analysis type toggle */}
        <div className="flex bg-[#1e293b] rounded-[6px] p-0.5 w-fit">
          {['PFS', 'OS'].map(opt => (
            <button key={opt} onClick={() => setAnalysisType(opt)}
              className={`px-3.5 py-1 rounded-[6px] text-[10px] font-bold tracking-wide transition-all ${
                analysisType === opt
                  ? 'bg-white text-[#1e293b] shadow'
                  : 'text-slate-300 hover:text-white'
              }`}>
              {opt}
            </button>
          ))}
        </div>
      </div>
      {/* Image display */}
      <div className="flex-1 overflow-y-auto p-6">
        {/* Data lock info */}
        <div className="mb-4 px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-lg">
          <p className="text-sm text-slate-600">
            <span className="font-semibold text-slate-700">Data lock:</span> May 2025 &nbsp;|&nbsp; <span className="font-semibold text-slate-700">Follow-up updated:</span> September 2025
          </p>
        </div>
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
            <span className="text-sm font-bold text-gray-700">{currentLabel}</span>
          </div>
          <img src={currentImage} alt={currentLabel} className="w-full h-auto" />
        </div>
      </div>
    </div>
  );
};

// --- Main App Component ---
export default function RWEDashboard() {
  console.log('🔄 RWEDashboard component rendering...');

  const [activeModule, setActiveModule] = useState('personas'); // 'personas' | 'benchmarking'
  const [activeIndication, setActiveIndication] = useState('NHL');
  const [showOldDataModal, setShowOldDataModal] = useState(false); // Previous data modal

  // Summary stats bubbled up from OverallCohortChart (shown below filter bar)
  const [cohortStats, setCohortStats] = useState(null);

  // ORR data fetched in parallel at dashboard level (not inside OverallCohortChart)
  const [dashboardOrrData, setDashboardOrrData] = useState(undefined); // undefined = loading, null = failed

  // Filter sidebar open/close — driven by context so Header button can toggle it
  const { isFilterOpen, toggleFilter, setIsFilterOpen } = useFilterContext();

  // Fetch data last updated date once on mount, independent of active module
  const [dataLastUpdated, setDataLastUpdated] = useState(null);
  useEffect(() => {
    post(API_ENDPOINTS.survivalAnalysis, {
      indication: 'all', analysis_type: 'pfs', filter_type: 'global',
      filter_value: 'all', lines_of_failure: 'all', timeline_months: 12,
      active_level: 'global'
    }).then(res => {
      if (res.data_last_followup_updated) {
        setDataLastUpdated(res.data_last_followup_updated);
      }
    }).catch(() => { });
  }, []);
  // Removed viewMode state since we only use card view now
  // isFilterOpen is now managed by FilterContext (toggled from Header)
  const [personaModalOpen, setPersonaModalOpen] = useState(false);

  const [filters, setFilters] = useState(() => {
    // Read the logged-in user synchronously from localStorage so that
    // role-specific defaults work immediately without waiting for rbacConfig to load
    const storedUser = getStoredUser();
    const role = storedUser?.role;
    const isPhysician = role === 'physician';
    const myPhysicianName = isPhysician ? (storedUser?.name || '') : '';
    return {
      timeline: isPhysician ? '3 Month' : '12 Month',
      // Always start on "Overall Evaluable Cohort" — the rbacConfig sync effect
      // will update activeLevel to 'my_patients' for physicians before the first
      // API fetch fires (gated on rbacReady in useSurvivalAnalysis)
      activeLevel: 'global',
      selectedInstitute: '',
      selectedRegion: '',
      selectedPhysician: '',
      myPhysicianName,   // Populated from localStorage for physician role
      comparator: 'Global CAR-T',
      selectedRegimen: '',
      analysisType: 'PFS',
      gradientScheme: 'clinical',
      apheresisCutoff: 'December 2025',  // Default to Dec 2025 on load
      followUpCutoff: 'April 2026'    // Default to Apr 2026 on load
    };
  });

  const [selectedPersona, setSelectedPersona] = useState(null);

  // Clear stale cohort stats immediately when filter scope changes
  useEffect(() => {
    setCohortStats(null);
  }, [
    filters.activeLevel,
    filters.selectedInstitute,
    filters.selectedRegion,
    filters.selectedPhysician,
    filters.myPhysicianName,
    activeIndication,
  ]);

  // RBAC filter config — controls what the filter panel shows per role
  const { config: rbacConfig } = useRbacFilters();

  // Gate: true once rbacConfig has been applied to filters.
  // Prevents useSurvivalAnalysis from firing a global fetch before the
  // physician's activeLevel has been switched to 'my_patients'.
  const [rbacApplied, setRbacApplied] = useState(false);

  const [selectedCard, setSelectedCard] = useState(null);

  // Validate followUpCutoff when apheresisCutoff changes
  useEffect(() => {
    if (filters.apheresisCutoff && filters.followUpCutoff) {
      // Parse both dates to compare them
      const parseDate = (dateStr) => {
        const [month, year] = dateStr.split(' ');
        const monthNum = new Date(Date.parse(month + " 1, 2000")).getMonth();
        return new Date(year, monthNum);
      };

      const apheresisDate = parseDate(filters.apheresisCutoff);
      const followUpDate = parseDate(filters.followUpCutoff);

      // If follow-up date is before apheresis date, clear it
      if (followUpDate < apheresisDate) {
        setFilters(prev => ({ ...prev, followUpCutoff: null }));
      }
    }
  }, [filters.apheresisCutoff, filters.followUpCutoff]);

  // Debug: Log when selectedCard changes
  useEffect(() => {
    console.log('🔄 selectedCard state changed:', selectedCard);
  }, [selectedCard]);

  // NEW: State for lines of failure KM curve data
  const [linesOfFailureData, setLinesOfFailureData] = useState(null);

  // ── Lines-of-failure cache state (populated after apiData loads below) ───────
  const [linesCache, setLinesCache] = useState({});
  const [overallLinesDataCache, setOverallLinesDataCache] = useState(null);

  useEffect(() => {
    // Only reset when filter LEVEL changes or when switching indications or date filters change
    // Don't reset when timeline or analysisType changes - keep the selected card
    console.log('🔄 Filter level, indication, or date filters changed - resetting selected card and persona');
    setSelectedCard(null);
    setSelectedPersona(null);
    setLinesOfFailureData(null);
    setLinesCache({});
    setOverallLinesDataCache(null);
  }, [
    filters.activeLevel,
    filters.selectedInstitute,
    filters.selectedRegion,
    filters.selectedPhysician,
    filters.apheresisCutoff,     // NEW: Reset when apheresis cutoff changes
    filters.followUpCutoff,      // NEW: Reset when follow-up cutoff changes
    activeIndication
    // Removed: filters.timeline, filters.analysisType - these should NOT reset selection
  ]);

  const {
    data: apiData,
    loading: apiLoading,
    error: apiError,
    apiHealthy,
    refetch: refetchData,
  } = useSurvivalAnalysis(filters, activeIndication, activeModule, rbacApplied);

  // ── ORR fetch — fires IN PARALLEL with main survival analysis ──────────────
  useEffect(() => {
    if (activeModule !== 'personas' || !rbacApplied) return;

    const shouldFetch =
      filters.activeLevel === 'global' ||
      (filters.activeLevel === 'institute' && filters.selectedInstitute) ||
      (filters.activeLevel === 'region' && filters.selectedRegion) ||
      (filters.activeLevel === 'physician' && filters.selectedPhysician) ||
      (filters.activeLevel === 'my_patients' && filters.myPhysicianName);

    if (!shouldFetch) return;

    let cancelled = false;
    setDashboardOrrData(undefined); // mark as loading
    fetchORRData(filters, activeIndication).then(result => {
      if (!cancelled) setDashboardOrrData(result);
    }).catch(() => {
      if (!cancelled) setDashboardOrrData(null);
    });
    return () => { cancelled = true; };
  }, [
    activeModule, rbacApplied, activeIndication,
    filters.activeLevel, filters.selectedInstitute,
    filters.selectedRegion, filters.selectedPhysician,
    filters.myPhysicianName, filters.apheresisCutoff,
  ]);

  // ── Build lines-of-failure cache — fires IN PARALLEL with main survival analysis ──
  // Does NOT wait for apiData. Uses the same filter payload as useSurvivalAnalysis.
  useEffect(() => {
    if (activeModule !== 'personas') return;
    if (!rbacApplied) return; // wait for RBAC filters to be applied first

    const shouldFetch =
      filters.activeLevel === 'global' ||
      (filters.activeLevel === 'institute' && filters.selectedInstitute) ||
      (filters.activeLevel === 'region' && filters.selectedRegion) ||
      (filters.activeLevel === 'physician' && filters.selectedPhysician) ||
      (filters.activeLevel === 'my_patients' && filters.myPhysicianName);

    if (!shouldFetch) return;

    const LINE_CATEGORIES = [
      { id: '1', label: '1st Line Failure' },
      { id: '2', label: '2nd Line Failure' },
      { id: '3', label: '3rd Line Failure' },
      { id: '3+', label: '3+ Lines Failure' },
    ];

    const timelineMonths = parseInt(filters.timeline.split(' ')[0]);
    const indication     = activeIndication === 'NHL' ? 'nhl' : 'all';
    const apiBase        = process.env.REACT_APP_API_URL || 'http://localhost:5000';

    let filter_type = 'global', filter_value = 'all';
    if (filters.activeLevel === 'institute' && filters.selectedInstitute)       { filter_type = 'account'; filter_value = filters.selectedInstitute; }
    else if (filters.activeLevel === 'region' && filters.selectedRegion)        { filter_type = 'zone';    filter_value = filters.selectedRegion; }
    else if (filters.activeLevel === 'physician' && filters.selectedPhysician)  { filter_type = 'hcp';     filter_value = filters.selectedPhysician; }
    else if (filters.activeLevel === 'my_patients' && filters.myPhysicianName)  { filter_type = 'hcp';     filter_value = filters.myPhysicianName; }

    // Map date filters
    let apheresis_cutoff = null;
    let last_date_of_followup_cutoff = null;
    if (filters.apheresisCutoff) {
      const [month, year] = filters.apheresisCutoff.split(' ');
      const monthNum = new Date(Date.parse(month + " 1, 2000")).getMonth() + 1;
      const lastDay = new Date(year, monthNum, 0).getDate();
      apheresis_cutoff = `${year}-${String(monthNum).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
    }
    if (filters.followUpCutoff) {
      const [month, year] = filters.followUpCutoff.split(' ');
      const monthNum = new Date(Date.parse(month + " 1, 2000")).getMonth() + 1;
      const lastDay = new Date(year, monthNum, 0).getDate();
      last_date_of_followup_cutoff = `${year}-${String(monthNum).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
    }

    const basePayload = {
      indication,
      analysis_type: filters.analysisType.toLowerCase(),
      timeline_months: timelineMonths,
      filter_type, filter_value,
      selected_institute: filters.selectedInstitute || '',
      selected_regions:   filters.selectedRegion   ? [filters.selectedRegion]   : [],
      selected_physicians: filters.activeLevel === 'my_patients' && filters.myPhysicianName
        ? [filters.myPhysicianName]
        : (filters.selectedPhysician ? [filters.selectedPhysician] : []),
      apheresis_cutoff,
      last_date_of_followup_cutoff,
    };

    Promise.all(
      LINE_CATEGORIES.map(cat =>
        fetch(`${apiBase}/survival-analysis`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...basePayload, lines_of_failure: cat.id }),
        })
          .then(r => r.ok ? r.json() : null)
          .then(data => ({ cat, data }))
          .catch(() => ({ cat, data: null }))
      )
    ).then(responses => {
      const cache = {};
      const overallLines = [];
      responses.forEach(({ cat, data }) => {
        const km = data?.overall_km_object || data?.overall_km || null;
        overallLines.push({
          id:            cat.id,
          label:         cat.label,
          description:   '',
          n:             km?.n || data?.cohort_size || 0,
          survival_rate: (() => {
            if (!km?.x || !km?.y) return 0;
            const idx = km.x.findIndex(t => t === timelineMonths);
            return idx !== -1 ? Math.round(km.y[idx] * 100) : 0;
          })(),
          kmData:        km ? { x: km.x, y: km.y, n: km.n, at_risk: km.at_risk || [], events: km.events || [], censored: km.censored || [], label: cat.label } : null,
        });
        if (!data?.persona_km_objects) return;
        data.persona_km_objects.forEach(p => {
          const label = p.label || p.persona;
          if (!label) return;
          if (!cache[label]) cache[label] = {};
          let survivalRate = 0, patientCount = p.n || 0, kmData = null;
          if (p.x && p.y) {
            const idx = p.x.findIndex(t => t === timelineMonths);
            if (idx !== -1) survivalRate = Math.round(p.y[idx] * 100);
            kmData = { x: p.x, y: p.y, n: p.n, at_risk: p.at_risk || [], events: p.events || [], censored: p.censored || [], label: cat.label };
          }
          cache[label][cat.id] = { id: cat.id, label: cat.label, description: '', n: patientCount, survival_rate: survivalRate, kmData };
        });
      });
      setLinesCache(cache);
      setOverallLinesDataCache(overallLines);
    }).catch(() => {});
  }, [
    activeModule, rbacApplied, activeIndication,
    filters.timeline, filters.analysisType, filters.activeLevel,
    filters.selectedInstitute, filters.selectedRegion,
    filters.selectedPhysician, filters.myPhysicianName,
    filters.apheresisCutoff, filters.followUpCutoff,
  ]);

  // Serve lines data from cache instantly when a persona is selected
  useEffect(() => {
    if (!selectedPersona?.persona) { setLinesOfFailureData(null); return; }
    const cached = linesCache[selectedPersona.persona];
    if (cached) {
      const LINE_IDS = ['1', '2', '3', '3+'];
      setLinesOfFailureData(LINE_IDS.map(id => cached[id] || { id, label: '', description: '', n: 0, survival_rate: 0, kmData: null }));
    } else {
      setLinesOfFailureData(null);
    }
  }, [selectedPersona?.persona, linesCache]);

  const { data: safetyData } = useSafetyProfile(filters, activeIndication, activeModule);

  const {
    filtersData,
    loading: filtersLoading,
    // error: filtersError
  } = useFiltersData();

  // Check if user needs to select a filter value
  const needsFilterSelection = useMemo(() => {
    if (activeModule !== 'personas') return { needed: false };

    if (filters.activeLevel === 'institute' && !filters.selectedInstitute) {
      if (rbacConfig?.singleHospitalMode) return { needed: false };
      return { needed: true, type: 'hospital', message: 'Please select a hospital from the dropdown to view data' };
    }
    if (filters.activeLevel === 'region' && !filters.selectedRegion) {
      return { needed: true, type: 'region', message: 'Please select a geographic region from the dropdown to view data' };
    }
    if (filters.activeLevel === 'physician' && !filters.selectedPhysician) {
      return { needed: true, type: 'physician', message: 'Please select a physician from the dropdown to view data' };
    }

    return { needed: false };
  }, [activeModule, filters.activeLevel, filters.selectedInstitute, filters.selectedRegion, filters.selectedPhysician, rbacConfig]);

  // Sync the logged-in physician's name into filters so "My Patients" can use it
  // Also auto-select the hospital when singleHospitalMode is true
  useEffect(() => {
    if (!rbacConfig) return;

    setFilters(prev => {
      const updates = {};

      // Sync physician name (confirm from server — localStorage may be stale)
      if (rbacConfig.role === 'physician' && rbacConfig.physicianName) {
        updates.myPhysicianName = rbacConfig.physicianName;
      }

      // Switch physician to "My Patients" on first load.
      // Only fires once — if they've already manually navigated away, don't override.
      if (rbacConfig.role === 'physician' && prev.activeLevel === 'global') {
        updates.activeLevel = 'my_patients';
      }

      // Auto-select the only hospital for singleHospitalMode sales users ONLY.
      // Physicians manage their own hospital selection manually.
      if (
        rbacConfig.singleHospitalMode &&
        rbacConfig.role !== 'physician' &&
        rbacConfig.allowedHospitals?.[0]?.name
      ) {
        const hospital = rbacConfig.allowedHospitals[0].name;
        if (prev.activeLevel !== 'institute' || prev.selectedInstitute !== hospital) {
          updates.activeLevel = 'institute';
          updates.selectedInstitute = hospital;
        }
      }

      return Object.keys(updates).length ? { ...prev, ...updates } : prev;
    });

    // Mark rbac as applied — unblocks the first API fetch in useSurvivalAnalysis.
    // setFilters + setRbacApplied are batched by React into one render, so the
    // fetch fires with the already-updated activeLevel ('my_patients').
    setRbacApplied(true);
  }, [rbacConfig]);

  React.useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      // Simple inline test
      const runQuickTest = async () => {
        console.log('🧪 Running quick API test...');
        try {
          const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/health`);
          const data = await response.json();
          console.log('✅ Quick test result:', data);
        } catch (error) {
          console.error('❌ Quick test failed:', error);
        }
      };

      window.testAPI = async () => {
        console.log('🧪 Testing API connection...');
        const result = await testAPIConnection();
        console.log('API Test Result:', result);
        return result;
      };

      // console.log(runQuickTest, "")

      window.testSurvival = async () => {
        console.log('🧪 Testing survival analysis...');
        try {
          const result = await testSurvivalAnalysis();
          console.log('Survival Test Result:', result);
          return result;
        } catch (error) {
          console.error('Survival Test Error:', error);
          return null;
        }
      };

      window.testBothEndpoints = async () => {
        console.log('🧪 Testing both PFS and OS endpoints...');
        try {
          const result = await testSurvivalAnalysis();
          console.log('📊 Both endpoints tested:', result);

          // Compare first KM object values
          if (result.pfs.km_objects[0] && result.os.km_objects[0]) {
            console.log('📈 PFS 12-month survival:', Math.round(result.pfs.km_objects[0].y[12] * 100) + '%');
            console.log('📈 OS 12-month survival:', Math.round(result.os.km_objects[0].y[12] * 100) + '%');
          }

          return result;
        } catch (error) {
          console.error('Test Error:', error);
          return null;
        }
      };

      window.quickTest = runQuickTest;

      window.testOverallCohort = testOverallCohortAPI;

      console.log('🔧 Debug functions loaded:');
      console.log('  - window.testAPI() - Test API health');
      console.log('  - window.testSurvival() - Test survival analysis');
      console.log('  - window.testBothEndpoints() - Test both PFS and OS');
      console.log('  - window.testOverallCohort() - Test overall cohort API');
      console.log('  - window.quickTest() - Quick connection test');

      // Auto-run quick test
      setTimeout(runQuickTest, 1000);
    }
  }, []);

  // Debug filters data loading
  React.useEffect(() => {
    if (filtersData && !filtersLoading) {
      console.log('🎯 Real filters data loaded:', {
        hospitals: filtersData.hospitals?.length || 0,
        physicians: filtersData.physicians?.length || 0,
        regions: filtersData.regions?.length || 0,
        totalRecords: filtersData.totalRecords || 0
      });
    }
  }, [filtersData, filtersLoading]);

  // Debug API data updates
  React.useEffect(() => {
    if (apiData) {
      console.log('📊 API Data Updated in RWEDashboard:', {
        totalPatients: apiData.totalPatients,
        activeModule,
        activeLevel: filters.activeLevel,
        selectedPhysician: filters.selectedPhysician,
        selectedInstitute: filters.selectedInstitute,
        selectedRegion: filters.selectedRegion,
        timestamp: new Date().toISOString()
      });
    }
  }, [apiData, activeModule, filters.activeLevel, filters.selectedPhysician, filters.selectedInstitute, filters.selectedRegion]);

  // Function to handle persona card clicks - NO MODAL
  const onCardClick = (cohortData, rowData, columnData) => {
    // Only update the chart to show persona data
    handlePersonaCardClick(cohortData, rowData, columnData);
  };

  // NEW: Function to handle persona card clicks
  const handlePersonaCardClick = (cohortData, rowData, columnData) => {
    console.log('🎯 handlePersonaCardClick called with:', { cohortData, rowData, columnData });

    try {
      // Note: do NOT reset linesOfFailureData here — prefetchLinesOfFailure will
      // overwrite it with fresh data for the new persona as soon as it resolves.
      setSelectedCard({
        rowId: rowData.id,
        colId: columnData.id,
        row: rowData,      // Store full row object
        col: columnData    // Store full column object
      });

      console.log('✅ setSelectedCard called with:', {
        rowId: rowData.id,
        colId: columnData.id
      });

      // Log to verify state will be updated
      console.log('📌 selectedCard state should now be set for Lines of Failure');

      // Construct persona title based on indication
      const personaTitle = activeIndication === 'NHL'
        ? `${rowData.title} | ${columnData.sub} | IPI ${columnData.label.includes('Low') ? 'Low' : 'High'} (${columnData.range.replace('-', '–')})`
        : `${rowData.title} | ${rowData.linesLabel} | ${columnData.sub}`;

      console.log('🎯 Persona card clicked:', {
        cohortData,
        rowData: rowData?.title,
        columnData: columnData?.label,
        columnData_full: columnData,
        'Constructed Persona Title': personaTitle,
        'Card Values': {
          pfs: cohortData.pfs,
          os: cohortData.os,
          n: cohortData.n
        }
      });

      // Check if this is a card with NO PATIENTS (not just 0% survival)
      // IMPORTANT: 0% survival with n > 0 means patients exist but didn't survive
      if (cohortData.n === 0) {
        console.log('⚠️ Clicked on card with no patients (n=0)');

        // Show a user-friendly message instead of trying to find KM data
        const personaIdentifier = createPersonaIdentifier(rowData, columnData);

        // Create mock data for display purposes
        const emptyPersonaData = {
          kmData: {
            n: 0,
            x: Array.from({ length: 25 }, (_, i) => i), // 0 to 24 months
            y: Array.from({ length: 25 }, () => 0) // All zeros
          },
          patients: 0,
          persona: personaIdentifier,
          title: rowData.title,
          description: `${rowData.desc} - ${columnData?.label || 'All patients'} (${columnData?.sub || ''}) - No patients in this subgroup`,
          survivalRate: 0,
          isEmpty: true // Flag to indicate this is an empty subgroup
        };

        setSelectedPersona(emptyPersonaData);
        setPersonaModalOpen(true);
        setIsFilterOpen(false); // Auto-close filter panel when modal opens
        console.log('📊 Created empty persona data for display:', emptyPersonaData);
        return;
      }

      // If we reach here, the card has patients (even if survival is 0%)
      console.log(`✅ Card has ${cohortData.n} patients (survival may be 0% but patients exist)`);

      // Find the corresponding persona KM data from API response
      if (apiData && apiData.persona_km_objects) {
        // CRITICAL DEBUG: Check what's in the persona_km_objects array
        console.log('🔍 DEBUG: apiData.persona_km_objects structure:', {
          count: apiData.persona_km_objects.length,
          firstObject: apiData.persona_km_objects[0],
          firstObjectKeys: Object.keys(apiData.persona_km_objects[0] || {}),
          hasAtRiskInFirst: !!(apiData.persona_km_objects[0] && apiData.persona_km_objects[0].at_risk),
          atRiskLengthInFirst: apiData.persona_km_objects[0] && apiData.persona_km_objects[0].at_risk ? apiData.persona_km_objects[0].at_risk.length : 'N/A'
        });

        // Create persona identifier to match with backend data
        const personaIdentifier = createPersonaIdentifier(rowData, columnData);
        console.log('🔍 Looking for persona:', personaIdentifier);
        console.log('🔍 Card data:', {
          pfs: cohortData.pfs,
          os: cohortData.os,
          n: cohortData.n,
          rowTitle: rowData.title,
          columnLabel: columnData?.label,
          columnSub: columnData?.sub,
          columnRange: columnData?.range,
          columnType: columnData?.type,
          columnId: columnData?.id
        });
        console.log('🔍 Available personas:', apiData.persona_km_objects.map(p => ({
          persona: p.persona || p.label,
          label: p.label,
          n: p.n,
          survival_12m: Math.round(p.y[12] * 100)
        })));

        // Find matching KM object with multiple matching strategies
        const matchingKM = apiData.persona_km_objects.find(kmObj => {
          // CRITICAL: Backend returns "label" field, not "persona"
          const kmPersona = kmObj.label || kmObj.persona;

          // Strategy 1: Exact persona string match (HIGHEST PRIORITY)
          const exactMatch = kmPersona === personaIdentifier;

          if (exactMatch) {
            console.log(`✅ EXACT MATCH FOUND: "${kmPersona}"`);
            return true;
          }

          // Strategy 2: Patient count match (most reliable for unique subgroups)
          const patientMatch = kmObj.n === cohortData.n;

          // Strategy 3: Partial match on all key components
          const titleMatch = kmPersona.includes(rowData.title);

          // For NHL, we need to match BOTH bulky/non-bulky AND IPI range
          let columnMatch = false;
          if (columnData) {
            if (activeIndication === 'NHL') {
              // Must match BOTH sub (Bulky/Non-Bulky) AND range (0-2 or 3-5)
              const subMatch = columnData.sub && kmPersona.includes(columnData.sub);
              const rangeMatch = columnData.range && (
                (columnData.range === '0-2' && kmPersona.includes('Low (0–2)')) ||
                (columnData.range === '3-5' && kmPersona.includes('High (3–5)'))
              );
              columnMatch = subMatch && rangeMatch;

              console.log(`  NHL Column Matching:`, {
                sub: columnData.sub,
                range: columnData.range,
                subMatch,
                rangeMatch,
                columnMatch
              });
            } else if (activeIndication === 'B-ALL') {
              // For B-ALL, match the three independent sub-groups by column id.
              // Backend labels: "... | MRD -ve", "... | MRD +ve", "... | Blast >=5%".
              // Order matters: test Blast before MRD +ve since they are distinct
              // independent labels (a Blast label does not contain "MRD +ve").
              columnMatch = (
                (columnData.type === 'blast' && (kmPersona.includes('Blast >=5%') || kmPersona.includes('Blast ≥5%'))) ||
                (columnData.type === 'mrd_neg' && kmPersona.includes('MRD -ve')) ||
                (columnData.type === 'mrd_pos' && kmPersona.includes('MRD +ve'))
              );
            }
          }

          const partialMatch = titleMatch && columnMatch;

          console.log(`🔍 Checking "${kmPersona}":`, {
            exactMatch,
            patientMatch,
            partialMatch,
            titleMatch,
            columnMatch,
            kmObj_n: kmObj.n,
            cohortData_n: cohortData.n,
            'Will match?': exactMatch || (patientMatch && partialMatch)
          });

          // STRICT MATCHING: Require both patient count AND partial match
          // This prevents matching wrong personas when patient counts are the same
          return patientMatch && partialMatch;
        });

        if (matchingKM) {
          const kmPersona = matchingKM.label || matchingKM.persona;

          // CRITICAL DEBUG: Check what's actually in matchingKM
          console.log('✅ Found matching KM data:', {
            persona: kmPersona,
            n: matchingKM.n,
            survival_12m: Math.round(matchingKM.y[12] * 100),
            '🔑 ALL KEYS in matchingKM': Object.keys(matchingKM),
            '📊 Has at_risk?': !!matchingKM.at_risk,
            '📊 at_risk length': matchingKM.at_risk ? matchingKM.at_risk.length : 'N/A',
            '📊 at_risk sample': matchingKM.at_risk ? matchingKM.at_risk.slice(0, 5) : 'N/A',
            '📦 Full matchingKM object': matchingKM
          });

          // Get the timeline value for survival rate calculation
          const timelineMonths = parseInt(filters.timeline.split(' ')[0]); // Extract months from "12 Month"

          const personaData = {
            kmData: matchingKM,
            patients: matchingKM.n,
            persona: kmPersona,
            title: rowData.title,
            description: `${rowData.desc} - ${columnData?.label || 'All patients'} (${columnData?.sub || ''})`,
            survivalRate: Math.round(matchingKM.y[timelineMonths] * 100) // Use selected timeline
          };

          // CRITICAL DEBUG: Check what's in personaData.kmData
          console.log('📦 Created personaData:', {
            '🔑 ALL KEYS in personaData': Object.keys(personaData),
            '🔑 ALL KEYS in personaData.kmData': Object.keys(personaData.kmData),
            '📊 personaData.kmData has at_risk?': !!personaData.kmData.at_risk,
            '📊 personaData.kmData.at_risk length': personaData.kmData.at_risk ? personaData.kmData.at_risk.length : 'N/A'
          });

          setSelectedPersona(personaData);
          setPersonaModalOpen(true);
          setIsFilterOpen(false); // Auto-close filter panel when modal opens
        } else {
          console.warn('❌ No matching KM data found for persona');
          console.warn('❌ Searched for:', personaIdentifier);
          console.warn('❌ Card data:', { n: cohortData.n, pfs: cohortData.pfs, os: cohortData.os });
          console.warn('❌ Available personas:', apiData.persona_km_objects.map(p => `"${p.label || p.persona}" (n=${p.n})`));

          // Show an alert to the user
          alert(`Unable to find matching survival curve data for this patient subgroup.\n\nSearched for: ${personaIdentifier}\nPatients: ${cohortData.n}\n\nThis may indicate a data mismatch between the card display and the survival analysis data.`);

          // Reset to overall cohort view if no match found
          setSelectedPersona(null);
        }
      } else {
        console.warn('❌ No API data available for persona matching');
        console.warn('❌ apiData exists:', !!apiData);
        console.warn('❌ persona_km_objects exists:', !!(apiData && apiData.persona_km_objects));

        // Show an alert to the user
        alert('No survival analysis data available. Please ensure the API is connected and data is loaded.');

        // Reset to overall cohort view if no API data
        setSelectedPersona(null);
      }
    } catch (error) {
      console.error('❌ Error in handlePersonaCardClick:', error);
      // Reset to overall cohort view on error
      setSelectedPersona(null);
    }
  };

  // Helper function to create persona identifier - MUST match backend format exactly
  const createPersonaIdentifier = (rowData, columnData) => {
    // For B-ALL, backend uses "Refractory" and "Relapsed" without "Population"
    let identifier = rowData.title;

    // Remove "Population" suffix for B-ALL to match backend format
    if (activeIndication === 'B-ALL') {
      identifier = identifier.replace(' Population', '');
      // Add lines label (Early/Late) for B-ALL
      if (rowData.linesLabel) {
        identifier += ` | ${rowData.linesLabel}`;
      }
    }

    if (columnData) {
      // Add column information - EXACT format matching backend
      if (columnData.sub === 'Non-Bulky') {
        identifier += ' | Non-Bulky';
      } else if (columnData.sub === 'Bulky') {
        identifier += ' | Bulky';
      }

      if (columnData.range) {
        if (columnData.range === '0-2') {
          identifier += ' | IPI Low (0-2)';  // Use regular hyphen to match backend
        } else if (columnData.range === '3-5') {
          identifier += ' | IPI High (3-5)';  // Use regular hyphen to match backend
        }
      }

      // For B-ALL (three independent sub-groups: MRD -ve / MRD +ve / Blast >=5%)
      if (columnData.type === 'mrd_neg') {
        identifier += ' | MRD -ve';
      } else if (columnData.type === 'mrd_pos') {
        identifier += ' | MRD +ve';
      } else if (columnData.type === 'blast') {
        identifier += ' | Blast >=5%';
      }
    }

    console.log('🏷️ Created persona identifier:', identifier);
    return identifier;
  };

  // NEW: Function to reset to overall cohort view
  const resetToOverallCohort = () => {
    try {
      console.log('🔄 Resetting to overall cohort view');
      setSelectedPersona(null);
      setSelectedCard(null); // Clear selected card border
      setLinesOfFailureData(null); // Clear lines of failure data
      setPersonaModalOpen(false);
    } catch (error) {
      console.error('❌ Error in resetToOverallCohort:', error);
    }
  };

  // NEW: Callback to receive lines of failure KM curve data
  const handleLinesDataLoaded = React.useCallback((linesData) => {
    console.log('📊 Lines of failure data loaded in RWEDashboard:', {
      count: linesData.length,
      lines: linesData.map(line => ({
        id: line.id,
        label: line.label,
        n: line.n,
        hasKmData: !!line.kmData
      }))
    });
    setLinesOfFailureData(linesData);
  }, []);

  // Modal functions removed - no longer using modal popup

  // --- Logic to Select Dataset ---
  const currentDataset = useMemo(() => {
    console.log('🔍 Selecting dataset:', {
      activeModule,
      activeIndication,
      apiData: apiData ? 'available' : 'not available',
      apiHealthy,
      apiLoading
    });

    // 1. For personas module, use API data if available, otherwise fallback to mock
    if (activeModule === 'personas') {
      // IMPORTANT: If loading, return null to show loading state
      if (apiLoading) {
        console.log('⏳ API is loading - returning null to show loading state');
        return null;
      }

      if (apiData && apiHealthy) {
        console.log('✅ Using API data for personas:', {
          totalPatients: apiData.totalPatients,
          overallCohort: apiData.overallCohort ? {
            totalPatients: apiData.overallCohort.totalPatients,
            pfs: apiData.overallCohort.pfs,
            os: apiData.overallCohort.os
          } : 'not available'
        });

        // Override desc labels for NHL rows regardless of what backend returns
        if (activeIndication === 'NHL' && apiData.rows) {
          const descOverrides = {
            refractory: 'No Response',
            early_relapse: '< 12 Months',
            late_relapse: '>= 12 Months',
          };
          return {
            ...apiData,
            rows: apiData.rows.map(row =>
              descOverrides[row.id] ? { ...row, desc: descOverrides[row.id] } : row
            ),
          };
        }

        return apiData;
      }

      // Do NOT fall back to mock data — return null so the error state renders
      console.log('⚠️ API error or unhealthy — returning null, error state will render');
      return null;
    }

    // 2. For benchmarking module, use mock data (can be extended later)
    const baseNexCAR = activeIndication === 'NHL' ? DATA_NHL_NEXCAR : DATA_BALL_NEXCAR;

    // Since we only have Global CAR-T now, generate benchmark data
    const modifier = 1.05;
    return generateBenchmarkData(baseNexCAR, modifier);
  }, [activeModule, activeIndication, apiData, apiHealthy, apiLoading]);

  // Comparison Reference (Always base data for Benchmarking view)
  const comparisonReference = useMemo(() => {
    if (activeModule === 'benchmarking') {
      return activeIndication === 'NHL' ? DATA_NHL_NEXCAR : DATA_BALL_NEXCAR;
    }
    return null;
  }, [activeModule, activeIndication]);

  return (
    <div className="min-h-screen bg-slate-50 font-sans text-slate-900">
      <FilterSidebar
        isOpen={isFilterOpen}
        toggle={toggleFilter}
        filters={filters}
        setFilters={setFilters}
        activeModule={activeModule}
        activeIndication={activeIndication}
        filtersData={filtersData}
        maxFollowUpDate={dataLastUpdated || apiData?.data_last_followup_updated}
        rbacConfig={rbacConfig}
      />

      <div className={`transition-all duration-300 ${
        activeModule === 'benchmarking' || isFilterOpen
          ? 'ml-64 md:ml-64 lg:ml-72'
          : 'ml-10'
      }`}>
        <div className="max-w-none px-3 md:px-4 lg:px-6 flex flex-col gap-y-3.5">
          {/* Active Filters - Sticky at top for Patient Personas */}
          {/* === Cohort Overview Banner === */}
          <div className="rounded-xl sticky top-2 z-50 flex flex-col gap-0 border border-slate-700 shadow-sm" style={{ backgroundColor: '#1e293b' }}>

            {/* Top row: title + breadcrumb (left) | toggles (right) */}
            <div className="px-5 py-3 flex flex-row items-center justify-between gap-4 flex-wrap border-b border-white/10">

              {/* Left: title + breadcrumb context */}
              <div className="flex flex-col min-w-0">
                {activeModule === 'personas' && (() => {
                  const levelNames = { 'global': 'Overall Cohort', 'region': 'Geographic Regions', 'institute': 'Hospitals', 'physician': 'Physicians', 'my_patients': 'My Patients' };
                  const handleReset = () => setFilters(prev => ({ ...prev, activeLevel: 'global', selectedInstitute: '', selectedRegion: '', selectedPhysician: '' }));
                  const effectiveLevel = filters.physicianMode ? 'my_patients' : filters.activeLevel;

                  // When a sub-selection is active, use the selected value as the heading
                  const selectedValue =
                    (filters.activeLevel === 'region'     && filters.selectedRegion)    ? filters.selectedRegion    :
                    (filters.activeLevel === 'institute'  && filters.selectedInstitute) ? filters.selectedInstitute :
                    (filters.activeLevel === 'physician'  && filters.selectedPhysician) ? filters.selectedPhysician :
                    null;
                  const levelLabel = filters.activeLevel === 'institute' && selectedValue
                    ? formatHospitalDisplay(selectedValue)
                    : (selectedValue || levelNames[effectiveLevel] || effectiveLevel);

                  return (
                    <>
                      <h2 className="text-lg font-bold text-white tracking-tight">{levelLabel}</h2>
                      <div className="flex items-center gap-1 mt-0.5 flex-wrap">
                        <span className="text-[11px] text-slate-300 font-medium">Patient Persona</span>
                        <span className="text-slate-500 text-[10px]">·</span>
                        <span className="text-[11px] text-slate-300 font-medium">{activeIndication}</span>
                        <span className="text-slate-500 text-[10px]">·</span>
                        <span className="text-[11px] text-slate-300 font-medium">{filters.analysisType}</span>
                        <span className="text-slate-500 text-[10px]">·</span>
                        <span className="text-[11px] text-slate-300 font-medium">{filters.timeline}</span>
                        {/* Heading shows the level/selected value; breadcrumb only repeats it when there's something to clear */}
                        {selectedValue ? (
                          <><span className="text-slate-500 text-[10px]">·</span>
                          <span className="text-amber-400 flex flex-row items-center justify-between gap-1 leading-5 font-medium text-xs">
                            {levelNames[effectiveLevel]}<button onClick={handleReset} aria-label="Clear"><X className="w-3 h-3" /></button>
                          </span></>
                        ) : effectiveLevel !== 'global' ? (
                          <><span className="text-slate-500 text-[10px]">·</span>
                          <span className="text-[11px] text-slate-300 font-medium">{levelLabel}</span></>
                        ) : null}
                      </div>
                    </>
                  );
                })()}
                {activeModule === 'benchmarking' && (
                  <h2 className="text-sm font-bold text-white leading-tight">Benchmarking</h2>
                )}
              </div>

              {/* Right: all toggles */}
              <div className="flex items-center gap-2 flex-shrink-0 flex-wrap justify-end">
                {/* Patient Personas / Benchmarking */}
                <div className="flex bg-white/10 rounded-[6px] p-0.5 w-fit">
                  <button onClick={() => { setActiveModule('personas'); setFilters(f => ({ ...f, timeline: '12 Month' })); }}
                    className={`px-3.5 py-1 rounded-[6px] text-[10px] font-bold tracking-wide transition-all ${activeModule === 'personas' ? 'bg-white text-[#1e293b] shadow' : 'text-slate-300 hover:text-white'}`}>
                    Patient Personas
                  </button>
                  <button onClick={() => { setActiveModule('benchmarking'); setIsFilterOpen(true); setFilters(f => ({ ...f, timeline: '12 Month' })); }}
                    className={`px-3.5 py-1 rounded-[6px] text-[10px] font-bold tracking-wide transition-all ${activeModule === 'benchmarking' ? 'bg-white text-[#1e293b] shadow' : 'text-slate-300 hover:text-white'}`}>
                    Benchmarking
                  </button>
                </div>

                <span className="w-px h-5 bg-white/15" />

                {/* NHL / B-ALL */}
                <div className="flex bg-white/10 rounded-[6px] p-0.5 w-fit">
                  {activeModule === 'personas' ? (
                    <>
                      <button onClick={() => setActiveIndication('NHL')}
                        className={`px-3.5 py-1 rounded-[6px] text-[10px] font-bold tracking-wide transition-all ${activeIndication === 'NHL' ? 'bg-white text-[#1e293b] shadow' : 'text-slate-300 hover:text-white'}`}>NHL</button>
                      <button onClick={() => setActiveIndication('B-ALL')}
                        className={`px-3.5 py-1 rounded-[6px] text-[10px] font-bold tracking-wide transition-all ${activeIndication === 'B-ALL' ? 'bg-white text-[#1e293b] shadow' : 'text-slate-300 hover:text-white'}`}>B-ALL</button>
                    </>
                  ) : (
                    <>
                      <button onClick={() => setActiveIndication('NHL')}
                        className={`px-3.5 py-1 rounded-[6px] text-[10px] font-bold tracking-wide transition-all ${activeIndication === 'NHL' ? 'bg-white text-[#1e293b] shadow' : 'text-slate-300 hover:text-white'}`}>NHL</button>
                      <button onClick={() => setActiveIndication('B-ALL')}
                        className={`px-3.5 py-1 rounded-[6px] text-[10px] font-bold tracking-wide transition-all ${activeIndication === 'B-ALL' ? 'bg-white text-[#1e293b] shadow' : 'text-slate-300 hover:text-white'}`}>B-ALL</button>
                    </>
                  )}
                </div>

                {/* PFS / OS — personas only */}
                {activeModule === 'personas' && (
                  <div className="flex bg-white/10 rounded-[6px] p-0.5 w-fit">
                    <button onClick={() => setFilters(prev => ({ ...prev, analysisType: 'PFS' }))}
                      className={`px-3.5 py-1 rounded-[6px] text-[10px] font-bold tracking-wide transition-all ${filters.analysisType === 'PFS' ? 'bg-white text-[#1e293b] shadow' : 'text-slate-300 hover:text-white'}`}>PFS</button>
                    <button onClick={() => setFilters(prev => ({ ...prev, analysisType: 'OS' }))}
                      className={`px-3.5 py-1 rounded-[6px] text-[10px] font-bold tracking-wide transition-all ${filters.analysisType === 'OS' ? 'bg-white text-[#1e293b] shadow' : 'text-slate-300 hover:text-white'}`}>OS</button>
                  </div>
                )}


              </div>
            </div>

            {/* Bottom row: inline metrics — personas only */}
            {activeModule === 'personas' && (
              <SummaryStatsBar stats={cohortStats ? { ...cohortStats, orrData: dashboardOrrData !== undefined ? dashboardOrrData : cohortStats.orrData } : null} dataset={currentDataset} />
            )}
          </div>

          {/* API Status Indicator */}
          {/* <APIStatus
            apiHealthy={apiHealthy}
            loading={apiLoading}
            error={apiError}
          /> */}

          {/* Debug: Filters Data Status */}
          {/* {process.env.NODE_ENV === 'development' && (
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 mb-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-bold text-slate-700">🔧 Filters Data Status (Dev Only)</h3>
                  <div className="text-xs text-slate-500 mt-1">
                    {filtersLoading ? (
                      <span className="text-amber-600">Loading filters data...</span>
                    ) : filtersError ? (
                      <span className="text-red-600">Error: {filtersError}</span>
                    ) : (
                      <span className="text-green-600">
                        ✅ Loaded: {filtersData?.hospitals?.length || 0} hospitals, {filtersData?.physicians?.length || 0} physicians, {filtersData?.regions?.length || 0} regions
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      console.log('🎯 Current filters data:', filtersData);
                      alert(`Hospitals: ${filtersData?.hospitals?.length || 0}\nPhysicians: ${filtersData?.physicians?.length || 0}\nRegions: ${filtersData?.regions?.length || 0}`);
                    }}
                    className="px-3 py-1 bg-slate-100 hover:bg-slate-200 rounded text-xs font-medium text-slate-600"
                  >
                    Debug Info
                  </button>
                  <button
                    onClick={async () => {
                      console.log('🧪 Manual API test started...');
                      console.log('Current filters:', filters);
                      
                      // Force a manual API call
                      try {
                        const result = await fetchSurvivalAnalysis(filters, activeIndication);
                        console.log('🎯 Manual API result:', result);
                        alert(`Manual test result: ${result.totalPatients} patients`);
                      } catch (error) {
                        console.error('❌ Manual API test failed:', error);
                        alert(`Manual test failed: ${error.message}`);
                      }
                    }}
                    className="px-3 py-1 bg-red-100 hover:bg-red-200 rounded text-xs font-medium text-red-600"
                  >
                    Manual API Test
                  </button>
                  <button
                    onClick={() => {
                      // Reset to global view
                      setFilters(prev => ({
                        ...prev,
                        activeLevel: 'global',
                        selectedInstitute: '',
                        selectedRegion: '',
                        selectedPhysician: ''
                      }));
                    }}
                    className="px-3 py-1 bg-green-100 hover:bg-green-200 rounded text-xs font-medium text-green-600"
                  >
                    Reset Filters
                  </button>
                </div>
              </div>
            </div>
          )} */}

          {/* Heat Map Legend - Only for Patient Personas */}
          {/* Disabled for now as per client request */}
          {/* {activeModule === 'personas' && (
            <HeatMapLegend scheme={filters.gradientScheme} />
          )} */}

          {/* Dynamic Content - Module-based Rendering */}
          <div className="min-h-[600px]">
            {activeModule === 'personas' ? (
              <>
                {/* Loading State - Show spinner while fetching data */}
                {apiLoading && (
                  <div className="flex flex-col items-center justify-center min-h-[400px] bg-white rounded-xl shadow-sm border border-slate-200 p-8">
                    <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-teal-600 mb-4"></div>
                    <h3 className="text-xl font-bold text-slate-800 mb-2">Loading Patient Data...</h3>
                    <p className="text-sm text-slate-600">Fetching survival analysis from backend</p>
                  </div>
                )}

                {/* Error State — single unified error when API fails */}
                {!apiLoading && (apiError || !apiHealthy) && !currentDataset && (
                  <div className="flex flex-col items-center justify-center min-h-[500px] bg-white rounded-xl shadow-sm border border-slate-200 p-12 animate-in fade-in duration-300">
                    {/* Icon */}
                    <div className="w-20 h-20 rounded-full bg-red-50 flex items-center justify-center mb-6">
                      <svg className="w-10 h-10 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
                      </svg>
                    </div>

                    {/* Title */}
                    <h3 className="text-xl font-bold text-slate-800 mb-2 text-center">
                      Unable to Load Patient Data
                    </h3>

                    {/* Message */}
                    <p className="text-sm text-slate-500 text-center max-w-md mb-2">
                      {!apiHealthy
                        ? 'The backend service is currently unavailable.'
                        : 'An error occurred while fetching data from the server.'}
                    </p>
                    <p className="text-xs text-slate-400 text-center max-w-md mb-8">
                      Please try again later.
                    </p>

                    {/* Retry button */}
                    <button
                      onClick={refetchData}
                      className="flex items-center gap-2 px-6 py-2.5 bg-teal-600 hover:bg-teal-700 text-white rounded-lg text-sm font-semibold transition-colors shadow-sm"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99" />
                      </svg>
                      Retry
                    </button>
                  </div>
                )}

                {/* Warning Banner - Show when using fallback data */}
                {/* {!apiLoading && !apiHealthy && currentDataset && (
                  <div className="bg-amber-50 rounded-xl shadow-sm border-2 border-amber-200 p-4 mb-6">
                    <div className="flex items-center gap-3">
                      <svg className="w-6 h-6 text-amber-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                      <div className="flex-1">
                        <h4 className="text-sm font-bold text-amber-900">Backend API Unavailable</h4>
                        <p className="text-xs text-amber-700 mt-1">Showing demo data for exploration. Real patient data is not available.</p>
                      </div>
                    </div>
                  </div>
                )} */}

                {/* Success Banner - Show when using real data */}
                {/* {!apiLoading && apiHealthy && apiData && (
                  <div className="bg-green-50 rounded-xl shadow-sm border border-green-200 p-3 mb-6">
                    <div className="flex items-center gap-3">
                      <svg className="w-5 h-5 text-green-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <div className="flex-1">
                        <p className="text-xs font-medium text-green-800">Connected to Backend - Showing Real Patient Data</p>
                      </div>
                    </div>
                  </div>
                )} */}

                {/* Show Filter Selection Required — only when backend is healthy */}
                {!apiLoading && apiHealthy && needsFilterSelection.needed ? (
                    <div className="flex flex-col items-center justify-center min-h-[500px] bg-white rounded-xl shadow-sm border-2 border-amber-200 p-12 animate-in fade-in slide-in-from-bottom-4 duration-500">
                      <div className="bg-amber-50 rounded-full p-6 mb-6">
                        <Filter className="w-16 h-16 text-amber-600" />
                      </div>
                      <h3 className="text-2xl font-bold text-slate-800 mb-3">Filter Selection Required</h3>
                      <p className="text-lg text-slate-600 mb-6 text-center max-w-md">
                        {needsFilterSelection.message}
                      </p>
                      <div className="flex items-center gap-2 text-sm text-slate-500">
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span>Use the filter panel on the left to make your selection</span>
                      </div>
                    </div>
                ) : null}
                {/* Show actual data when filter is selected or not needed */}
                {!apiLoading && !needsFilterSelection.needed && currentDataset && (
                    activeIndication === 'NHL'
                      ? <PersonasNHLView
                        timeline={filters.timeline}
                        dataset={currentDataset}
                        comparisonDataset={comparisonReference}
                        onCardClick={onCardClick}
                        analysisType={filters.analysisType}
                        gradientScheme={filters.gradientScheme}
                        selectedPersona={selectedPersona}
                        onResetToOverall={resetToOverallCohort}
                        activeIndication={activeIndication}
                        selectedCard={selectedCard}
                        linesOfFailureData={linesOfFailureData}
                        onLinesDataLoaded={handleLinesDataLoaded}
                        filters={filters}
                        safetyData={safetyData}
                        hideHospitalDistribution={
                          (rbacConfig?.role === 'sales' || rbacConfig?.role === 'physician') &&
                          filters.activeLevel !== 'global' &&
                          filters.activeLevel !== 'region'
                        }
                        onStatsReady={setCohortStats}
                        userRole={rbacConfig?.role}
                        preloadedOverallLinesData={overallLinesDataCache}
                        onShowPreviousData={() => setShowOldDataModal(true)}
                      />
                      : <PersonasBALLView
                        timeline={filters.timeline}
                        dataset={currentDataset}
                        comparisonDataset={comparisonReference}
                        onCardClick={onCardClick}
                        analysisType={filters.analysisType}
                        gradientScheme={filters.gradientScheme}
                        selectedPersona={selectedPersona}
                        onResetToOverall={resetToOverallCohort}
                        activeIndication={activeIndication}
                        selectedCard={selectedCard}
                        linesOfFailureData={linesOfFailureData}
                        onLinesDataLoaded={handleLinesDataLoaded}
                        filters={filters}
                        safetyData={safetyData}
                        hideHospitalDistribution={
                          (rbacConfig?.role === 'sales' || rbacConfig?.role === 'physician') &&
                          filters.activeLevel !== 'global' &&
                          filters.activeLevel !== 'region'
                        }
                        onStatsReady={setCohortStats}
                        userRole={rbacConfig?.role}
                        preloadedOverallLinesData={overallLinesDataCache}
                        onShowPreviousData={() => setShowOldDataModal(true)}
                      />
                )}
              </>
            ) : (
              // BENCHMARKING MODULE
              <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
                {filters.comparator === 'SOC' ? (
                  <SOCBenchmark indication={activeIndication} />
                ) : (
                  /* GLOBAL CAR-T BENCHMARK - Show Global2 component */
                  <Global2 indication={activeIndication} />
                )}
              </div>
            )}

            {/* DEBUG: Log dataset structure */}
            {process.env.NODE_ENV === 'development' && (
              <div style={{ display: 'none' }}>
                {console.log('🔍 Current dataset structure:', {
                  totalPatients: currentDataset?.totalPatients,
                  overallCohort: currentDataset?.overallCohort,
                  hasOverallCohort: !!currentDataset?.overallCohort,
                  overallCohortKeys: currentDataset?.overallCohort ? Object.keys(currentDataset.overallCohort) : 'none'
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal removed - using inline chart view only */}

      {/* Dashboard Patient Table — shown when filter is scoped to specific patients */}
      {activeModule === 'personas' && (() => {
        const level = filters.activeLevel;
        const userRole = rbacConfig?.role;

        // Superadmin & Stakeholder: show at ALL analysis levels
        // Sales & Physician: show only at institute, physician, my_patients levels
        let showTable = false;
        if (userRole === 'superadmin' || userRole === 'stakeholder') {
          // Always show — at global/region show all patients, at others show filtered
          showTable = true;
        } else {
          showTable =
            (level === 'institute' && filters.selectedInstitute) ||
            (level === 'physician' && filters.selectedPhysician) ||
            (level === 'my_patients');
        }

        if (!showTable) return null;

        return (
          <div className="mt-6 mx-4 pb-6 w-full overflow-hidden">
            <InlinePatientTable
              indication={activeIndication}
              role={rbacConfig?.role}
              personaLabel={null}
              hospital={level === 'institute' ? filters.selectedInstitute : null}
              filterPhysician={
                level === 'my_patients' ? filters.myPhysicianName :
                level === 'physician' ? filters.selectedPhysician :
                // Physician role at hospital level — scope to own patients only
                (level === 'institute' && userRole === 'physician' && filters.myPhysicianName) ? filters.myPhysicianName :
                null
              }
              activeFilters={filters}
            />
          </div>
        );
      })()}

      {/* Persona Deep Dive Modal — opens on card click */}
      <PersonaDeepDiveModal
        isOpen={personaModalOpen}
        onClose={() => {
          setPersonaModalOpen(false);
          resetToOverallCohort();
        }}
        selectedPersona={selectedPersona}
        selectedCard={selectedCard}
        overallCohortData={currentDataset?.overallCohort || null}
        linesOfFailureData={linesOfFailureData}
        onLinesDataLoaded={handleLinesDataLoaded}
        safetyData={safetyData}
        filters={filters}
        indication={activeIndication}
        analysisType={filters.analysisType}
        timeline={filters.timeline}
        gradientScheme={filters.gradientScheme}
        rbacConfig={rbacConfig}
        physicianName={rbacConfig?.physicianName || filters.myPhysicianName || ''}
      />

      {/* Previous Data Modal */}
      {showOldDataModal && createPortal(
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={() => setShowOldDataModal(false)}>
          <div className="bg-white rounded-xl shadow-2xl flex flex-col overflow-hidden w-[95vw] max-w-4xl max-h-[90vh]" onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
              <div>
                <h2 className="text-lg font-bold text-gray-900">Previous Data Snapshots</h2>
                <p className="text-xs text-gray-500 mt-0.5">Historical dashboard screenshots from production</p>
              </div>
              <button onClick={() => setShowOldDataModal(false)} className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors">
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            {/* Toggle + Image */}
            <OldDataModalContent initialIndication={activeIndication} initialAnalysisType={filters.analysisType} />
          </div>
        </div>,
        document.body
      )}
    </div>
  );
} 